import { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Card from '@mui/material/Card';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TablePagination from '@mui/material/TablePagination';
import TableSortLabel from '@mui/material/TableSortLabel';
import Chip from '@mui/material/Chip';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import IconButton from '@mui/material/IconButton';
import Button from '@mui/material/Button';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Autocomplete from '@mui/material/Autocomplete';
import Menu from '@mui/material/Menu';
import Checkbox from '@mui/material/Checkbox';
import FormGroup from '@mui/material/FormGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import Tooltip from '@mui/material/Tooltip';
import Collapse from '@mui/material/Collapse';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Drawer from '@mui/material/Drawer';
import { alpha, styled } from '@mui/material/styles';
import { CheckCircle2, FileDown, FileText, Mail, WalletCards, Calculator, Search, Phone, User, TrendingUp, TrendingDown, HelpCircle, AlertTriangle, XCircle, Eye, ExternalLink, Trash2 } from 'lucide-react';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import ViewColumnIcon from '@mui/icons-material/ViewColumn';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import LoaderProgress from '../components/ui/loader-progress';
import api from '../services/api';
import { useJob } from '../context/JobContext';
import { toast } from 'react-toastify';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend
} from 'recharts';

const StyledCard = styled(Card)(({ theme }) => ({
  boxShadow: '0 0 2px 0 rgba(145, 158, 171, 0.2), 0 12px 24px -4px rgba(145, 158, 171, 0.12)',
  borderRadius: 16,
  overflow: 'hidden',
  backgroundColor: theme.palette.background.paper,
  border: `1px solid ${theme.palette.divider}`
}));

const richiestaDocStatusMeta = (stato, allPresent, docTypeCnt = 0, requiredTotal = 4) => {
  const s = String(stato || '').trim().toLowerCase();
  const cnt = Number(docTypeCnt || 0);
  const total = Number(requiredTotal || 0) > 0 ? Number(requiredTotal) : 4;
  
  if (allPresent) {
    if (s === 'predisposta') return { label: 'Richiesta doc: predisposta (Completa)', Icon: CheckCircle2, color: '#10b981', bg: '#10b9811F' };
    if (s === 'inviata') return { label: 'Richiesta doc: inviata (Completa)', Icon: CheckCircle2, color: '#10b981', bg: '#10b9811F' };
    if (s === 'non_gestire') return { label: 'Richiesta doc: non gestire', Icon: XCircle, color: '#64748b', bg: '#64748b1F' };
    return { label: 'Richiesta doc: completa', Icon: CheckCircle2, color: '#10b981', bg: '#10b9811F' };
  }

  if (cnt > 0 && cnt < total) {
    return { label: `Documenti: caricati parzialmente (${cnt}/${total})`, Icon: AlertTriangle, color: '#f59e0b', bg: '#f59e0b1F' };
  }

  if (s === 'predisposta') {
    return { label: 'Richiesta doc: predisposta', Icon: AlertTriangle, color: '#f59e0b', bg: '#f59e0b1F' };
  }
  if (s === 'inviata') {
    return { label: 'Richiesta doc: inviata', Icon: Mail, color: '#2563eb', bg: '#2563eb1F' };
  }
  if (s === 'non_gestire') {
    return { label: 'Richiesta doc: non gestire', Icon: XCircle, color: '#64748b', bg: '#64748b1F' };
  }
  return { label: 'Documenti: mancanti', Icon: HelpCircle, color: '#ef4444', bg: '#ef44441F' };
};

const BASE_FILTERS = {
  search: '',
  tipoContratto: ['contratto', 'upsell'],
  statoPreventivo: ['accettato'],
  statoContratto: ['processing'],
  respComm: '',
  respUpsell: '',
  statoDocumenti: '',
  dataConferimentoFrom: '',
  dataConferimentoTo: '',
  dataInvioDocumentiFrom: '',
  dataInvioDocumentiTo: ''
};

const loadDefaultFilters = () => {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  const todayYmd = `${year}-${month}-${day}`;
  const firstDayOfMonthYmd = `${year}-${month}-01`;

  return {
    ...BASE_FILTERS,
    dataConferimentoFrom: firstDayOfMonthYmd,
    dataConferimentoTo: todayYmd,
    dataInvioDocumentiFrom: '',
    dataInvioDocumentiTo: ''
  };
};

const formatDate = (value) => {
  if (!value) return '-';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '-';
  return d.toLocaleDateString('it-IT');
};

const formatDateTime = (value) => {
  if (!value) return '-';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '-';
  return d.toLocaleDateString('it-IT');
};

const formatMoney = (value) => {
  if (value == null || value === '') return '-';
  const n = Number(value);
  if (Number.isNaN(n)) return '-';
  return new Intl.NumberFormat('it-IT', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 2
  }).format(n);
};

const formatPercent = (value) => {
  if (value == null || value === '') return '-';
  const n = Number(value);
  if (Number.isNaN(n)) return '-';
  return `${n}%`;
};

const getMoneyColor = (value) => {
  const n = Number(value);
  if (Number.isNaN(n)) return 'text.secondary';
  if (n > 0) return '#059669';
  if (n < 0) return '#dc2626';
  return 'text.secondary';
};

const renderPercentRing = (value) => {
  const num = Number(value);
  const clamped = Number.isFinite(num) ? Math.max(0, Math.min(100, num)) : 0;
  const label = `${Math.round(clamped)}%`;

  return (
    <Box sx={{ position: 'relative', display: 'inline-flex' }}>
      <CircularProgress
        variant="determinate"
        value={100}
        size={42}
        thickness={5}
        sx={{ color: 'grey.200' }}
      />
      <CircularProgress
        variant="determinate"
        value={clamped}
        size={42}
        thickness={5}
        sx={{
          position: 'absolute',
          left: 0,
          color: clamped >= 100 ? 'success.main' : 'warning.main'
        }}
      />
      <Box
        sx={{
          top: 0,
          left: 0,
          bottom: 0,
          right: 0,
          position: 'absolute',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}
      >
        <Typography component="span" sx={{ fontSize: 12, fontWeight: 800 }}>
          {label}
        </Typography>
      </Box>
    </Box>
  );
};

const RecallStatWidget = ({ title, value, icon: Icon, color }) => (
  <Paper
    elevation={0}
    variant="outlined"
    sx={(theme) => ({
      p: 2,
      display: 'flex',
      alignItems: 'center',
      height: '100%',
      borderColor:
        theme.palette.mode === 'dark'
          ? alpha(theme.palette.common.white, 0.14)
          : '#e2e8f0',
      borderRadius: 2,
      bgcolor:
        theme.palette.mode === 'dark'
          ? alpha(theme.palette.background.paper, 0.78)
          : '#ffffff'
    })}
  >
    <Box
      sx={{
        p: 1.5,
        borderRadius: '50%',
        bgcolor: `${color}15`,
        color,
        mr: 2,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}
    >
      <Icon size={24} />
    </Box>
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
        {title}
      </Typography>
      <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'text.primary', lineHeight: 1.2 }}>
        {value}
      </Typography>
    </Box>
  </Paper>
);

const getCustomerDisplayName = (row) => {
  if (row.customer_ragione_sociale) return row.customer_ragione_sociale;
  const fullName = [row.customer_name, row.customer_surname].filter(Boolean).join(' ').trim();
  if (fullName) return fullName;
  return row.rif_customer ? `ID ${row.rif_customer}` : '-';
};

const isAziendaCustomer = (row) => Boolean(String(row?.customer_ragione_sociale || '').trim());

const toArray = (value) => {
  if (Array.isArray(value)) return value.filter(Boolean);
  if (typeof value === 'string' && value.trim()) return [value.trim()];
  return [];
};

const buildCommercialiFiltersParams = (filtersArg) => ({
  search: filtersArg.search || '',
  tipoContratto: toArray(filtersArg.tipoContratto).join(','),
  statoPreventivo: toArray(filtersArg.statoPreventivo).join(','),
  statoContratto: toArray(filtersArg.statoContratto).join(','),
  respComm: filtersArg.respComm || '',
  respUpsell: filtersArg.respUpsell || '',
  statoDocumenti: filtersArg.statoDocumenti || '',
  dataConferimentoFrom: filtersArg.dataConferimentoFrom || '',
  dataConferimentoTo: filtersArg.dataConferimentoTo || '',
  dataInvioDocumentiFrom: filtersArg.dataInvioDocumentiFrom || '',
  dataInvioDocumentiTo: filtersArg.dataInvioDocumentiTo || ''
});

const resolveIncludeFinancials = (visibility) =>
  Boolean(
    visibility?.contrattiImporto ||
      visibility?.upsellImporto ||
      visibility?.incassatoImporto ||
      visibility?.incassatoPercent ||
      visibility?.saldoDaIncassare
  );

const resolveTrendMonth = (filtersArg) => {
  const candidate =
    String(filtersArg?.dataConferimentoTo || '').trim() ||
    String(filtersArg?.dataConferimentoFrom || '').trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(candidate)) {
    return candidate.slice(0, 7);
  }
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
};

const normalizeTrendDayKey = (rawDay) => {
  if (!rawDay) return '';
  const s = String(rawDay).trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
  const parsed = new Date(s);
  if (Number.isNaN(parsed.getTime())) return '';
  const y = parsed.getFullYear();
  const m = String(parsed.getMonth() + 1).padStart(2, '0');
  const d = String(parsed.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

const shiftIsoDateByMonths = (isoDate, monthsDelta) => {
  if (!isoDate) return '';
  const parsed = new Date(`${isoDate}T00:00:00`);
  if (Number.isNaN(parsed.getTime())) return '';
  const originalDay = parsed.getDate();
  parsed.setDate(1);
  parsed.setMonth(parsed.getMonth() + monthsDelta);
  const lastDayOfTargetMonth = new Date(parsed.getFullYear(), parsed.getMonth() + 1, 0).getDate();
  parsed.setDate(Math.min(originalDay, lastDayOfTargetMonth));
  const y = parsed.getFullYear();
  const m = String(parsed.getMonth() + 1).padStart(2, '0');
  const d = String(parsed.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

const detectRichiestaDocTypeFromName = (filename) => {
  const name = String(filename || '').toLowerCase();
  if (!name) return null;
  if (name.includes('contratto')) return 'contratto';
  if (name.includes('mandato')) return 'mandato';
  if (name.includes('questionario')) return 'questionario';
  if (name.includes('visura')) return 'visura';
  if (
    name.includes('doc') &&
    (name.includes('id') || name.includes('ident') || name.includes('carta') || name.includes('ci'))
  ) {
    return 'doc_identita';
  }
  if (name.includes('ident') || name.includes('carta') || name.includes('ci')) return 'doc_identita';
  return null;
};

const richiestaDocTypeLabel = (type) => {
  if (type === 'contratto') return 'Contratto';
  if (type === 'mandato') return 'Mandato';
  if (type === 'doc_identita') return 'Doc identità';
  if (type === 'visura') return 'Visura Azienda';
  if (type === 'questionario') return 'Questionario';
  return type;
};

const formatFileSize = (bytes) => {
  const size = Number(bytes || 0);
  if (!Number.isFinite(size) || size <= 0) return '-';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  let value = size;
  let unitIndex = 0;
  while (value >= 1024 && unitIndex < units.length - 1) {
    value /= 1024;
    unitIndex += 1;
  }
  const digits = value >= 100 ? 0 : value >= 10 ? 1 : 1;
  return `${value.toFixed(digits)} ${units[unitIndex]}`;
};

const guessMimeFromFilename = (filename) => {
  const name = String(filename || '').toLowerCase();
  if (name.endsWith('.pdf')) return 'application/pdf';
  if (name.endsWith('.jpg') || name.endsWith('.jpeg')) return 'image/jpeg';
  if (name.endsWith('.png')) return 'image/png';
  if (name.endsWith('.gif')) return 'image/gif';
  if (name.endsWith('.webp')) return 'image/webp';
  return 'application/octet-stream';
};

export default function CommercialiChiusure() {
  const { addJob } = useJob();
  const fetchSeqRef = useRef(0);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [total, setTotal] = useState(0);
  const [stats, setStats] = useState({
    totaleContratti: 0,
    importoTotale: 0,
    mediaImporto: 0
  });
  const [comparisonStats, setComparisonStats] = useState({
    previousTotal: 0,
    deltaPct: 0,
    deltaAbs: 0
  });
  const [commercialeDistribution, setCommercialeDistribution] = useState([]);
  const [monthlyTrend, setMonthlyTrend] = useState({ trendMonth: '', data: [] });
  const [yearComparison, setYearComparison] = useState({
    currentYear: new Date().getFullYear(),
    previousYear: new Date().getFullYear() - 1,
    data: []
  });
  const [viewTab, setViewTab] = useState('list');
  const [orderBy, setOrderBy] = useState('data_conferimento');
  const [order, setOrder] = useState('desc');
  const [contractTypeOptions, setContractTypeOptions] = useState([]);
  const [statoPreventivoOptions, setStatoPreventivoOptions] = useState([]);
  const [statoContrattoOptions, setStatoContrattoOptions] = useState([]);
  const [respCommOptions, setRespCommOptions] = useState([]);
  const [respUpsellOptions, setRespUpsellOptions] = useState([]);
  const [filters, setFilters] = useState(BASE_FILTERS);
  const [appliedFilters, setAppliedFilters] = useState(BASE_FILTERS);
  const [searchInput, setSearchInput] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [columnMenuAnchorEl, setColumnMenuAnchorEl] = useState(null);
  const [expandedRows, setExpandedRows] = useState({});
  const [rowNegotiations, setRowNegotiations] = useState({});
  const [rowNegotiationsLoading, setRowNegotiationsLoading] = useState({});
  const [reportDialogOpen, setReportDialogOpen] = useState(false);
  const [reportLoading, setReportLoading] = useState(false);
  const [csvLoading, setCsvLoading] = useState(false);
  const [firmeValidationLoading, setFirmeValidationLoading] = useState(false);
  const [firmeValidationConfirmOpen, setFirmeValidationConfirmOpen] = useState(false);
  const [firmeValidationDialogOpen, setFirmeValidationDialogOpen] = useState(false);
  const [firmeValidationSummary, setFirmeValidationSummary] = useState(null);
  const [firmeValidationMap, setFirmeValidationMap] = useState({});
  const [firmeValidationResults, setFirmeValidationResults] = useState([]);
  const [includeReportDetail, setIncludeReportDetail] = useState(true);
  const [columnVisibility, setColumnVisibility] = useState({
    responsabileCommerciale: true,
    responsabileUpsell: true,
    customer: true,
    funnelixConvalida: false,
    tipoContratto: true,
    statoPreventivo: true,
    dataConferimento: true,
    importoTotale: true,
    contrattiImporto: false,
    upsellImporto: false,
    incassatoImporto: true,
    incassatoPercent: true,
    saldoDaIncassare: false,
    acconto: true,
    percentageTransition: true,
    dataInvioDocumenti: true,
    dataChiusuraConferimento: false
  });

  const [richiestaDocDialogOpen, setRichiestaDocDialogOpen] = useState(false);
  const [richiestaDocTarget, setRichiestaDocTarget] = useState(null);
  const [richiestaDocFiles, setRichiestaDocFiles] = useState({
    contratto: null,
    mandato: null,
    doc_identita: null,
    visura: null,
    questionario: null
  });
  const [richiestaDocExisting, setRichiestaDocExisting] = useState(null);
  const [richiestaDocExistingLoading, setRichiestaDocExistingLoading] = useState(false);
  const [richiestaDocDuplicates, setRichiestaDocDuplicates] = useState({});
  const [richiestaDocUnmatched, setRichiestaDocUnmatched] = useState([]);
  const [richiestaDocSaving, setRichiestaDocSaving] = useState(false);
  const [richiestaDocDeleting, setRichiestaDocDeleting] = useState(false);
  const [richiestaDocDeleteConfirmOpen, setRichiestaDocDeleteConfirmOpen] = useState(false);
  const richiestaDocFileRef = useRef(null);
  const [richiestaDocPreviewOpen, setRichiestaDocPreviewOpen] = useState(false);
  const [richiestaDocPreviewUrl, setRichiestaDocPreviewUrl] = useState('');
  const [richiestaDocPreviewLoading, setRichiestaDocPreviewLoading] = useState(false);
  const [richiestaDocPreviewRow, setRichiestaDocPreviewRow] = useState(null);
  const [richiestaDocPreviewOpenInNewTabLoading, setRichiestaDocPreviewOpenInNewTabLoading] = useState(false);

  const richiestaDocIsAzienda = useMemo(
    () => isAziendaCustomer(richiestaDocTarget || {}),
    [richiestaDocTarget?.customer_ragione_sociale, richiestaDocTarget?.id]
  );

  const richiestaDocRequiredTypes = useMemo(
    () => (richiestaDocIsAzienda ? ['contratto', 'mandato', 'doc_identita', 'visura'] : ['contratto', 'mandato', 'doc_identita']),
    [richiestaDocIsAzienda]
  );

  const richiestaDocOptionalTypes = useMemo(() => ['questionario'], []);

  const richiestaDocAllTypes = useMemo(() => {
    const required = Array.isArray(richiestaDocRequiredTypes) ? richiestaDocRequiredTypes : [];
    const optional = Array.isArray(richiestaDocOptionalTypes) ? richiestaDocOptionalTypes : [];
    return [...required, ...optional.filter((t) => !required.includes(t))];
  }, [richiestaDocOptionalTypes, richiestaDocRequiredTypes]);

  const richiestaDocRequiredTotal = richiestaDocRequiredTypes.length;

  const richiestaDocPresentCount = useMemo(() => {
    const existing = richiestaDocExisting || {};
    return richiestaDocRequiredTypes.filter((type) => {
      if (type === 'contratto') return Boolean(richiestaDocFiles?.contratto || existing?.contratto_file_path);
      if (type === 'mandato') return Boolean(richiestaDocFiles?.mandato || existing?.mandato_file_path);
      if (type === 'doc_identita') return Boolean(richiestaDocFiles?.doc_identita || existing?.doc_identita_file_path);
      if (type === 'visura') return Boolean(richiestaDocFiles?.visura || existing?.visura_file_path);
      return false;
    }).length;
  }, [richiestaDocFiles, richiestaDocExisting, richiestaDocRequiredTypes]);

  const richiestaDocIsPartial =
    richiestaDocPresentCount > 0 && richiestaDocPresentCount < richiestaDocRequiredTotal;

  const fetchFilterOptions = async () => {
    try {
      const res = await api.get('/dossier/filters/options');
      const data = res.data || {};
      setContractTypeOptions(Array.isArray(data.contractTypes) ? data.contractTypes : []);
      setStatoPreventivoOptions(Array.isArray(data.statiPreventivo) ? data.statiPreventivo : []);
      setStatoContrattoOptions(Array.isArray(data.statuses) ? data.statuses : []);
      setRespCommOptions(Array.isArray(data.responsabiliCommerciali) ? data.responsabiliCommerciali : []);
      setRespUpsellOptions(Array.isArray(data.responsabiliUpsell) ? data.responsabiliUpsell : []);
    } catch (error) {
      console.error('Errore caricamento filtri commerciali chiusure:', error);
      setContractTypeOptions([]);
      setStatoPreventivoOptions([]);
      setStatoContrattoOptions([]);
      setRespCommOptions([]);
      setRespUpsellOptions([]);
    }
  };

  const fetchRows = async (
    pageArg = page,
    rowsArg = rowsPerPage,
    orderByArg = orderBy,
    orderArg = order,
    filtersArg = appliedFilters,
    visibilityArg = columnVisibility
  ) => {
    const seq = fetchSeqRef.current + 1;
    fetchSeqRef.current = seq;
    setLoading(true);
    try {
      const baseFiltersParams = buildCommercialiFiltersParams(filtersArg);
      const trendMonth = resolveTrendMonth(filtersArg);
      const referenceYear = Number(String(trendMonth).slice(0, 4)) || new Date().getFullYear();
      const includeFinancials = resolveIncludeFinancials(visibilityArg);
      const res = await api.get('/dossier/commerciali-chiusure', {
        params: {
          page: pageArg + 1,
          limit: rowsArg,
          orderBy: orderByArg,
          order: orderArg,
          ...(includeFinancials ? { include_financials: 'true' } : {}),
          ...baseFiltersParams
        },
        timeout: 20000
      });
      if (fetchSeqRef.current !== seq) return;
      const currentTotal = Number(res.data?.stats?.totaleContratti || 0);

      setRows(Array.isArray(res.data?.data) ? res.data.data : []);
      setTotal(Number(res.data?.total || 0));
      setStats({
        totaleContratti: currentTotal,
        importoTotale: Number(res.data?.stats?.importoTotale || 0),
        mediaImporto: Number(res.data?.stats?.mediaImporto || 0)
      });

      void (async () => {
        const previousFilters = {
          ...filtersArg,
          dataConferimentoFrom: shiftIsoDateByMonths(filtersArg.dataConferimentoFrom, -1),
          dataConferimentoTo: shiftIsoDateByMonths(filtersArg.dataConferimentoTo, -1)
        };
        try {
          const prevRes = await api.get('/dossier/commerciali-chiusure', {
            params: {
              page: 1,
              limit: 1,
              orderBy: orderByArg,
              order: orderArg,
              ...buildCommercialiFiltersParams(previousFilters)
            },
            timeout: 20000
          });
          if (fetchSeqRef.current !== seq) return;
          const previousTotal = Number(prevRes.data?.stats?.totaleContratti || 0);
          const deltaAbs = currentTotal - previousTotal;
          const deltaPct =
            previousTotal === 0 ? (currentTotal > 0 ? 100 : 0) : (deltaAbs / previousTotal) * 100;
          setComparisonStats({ previousTotal, deltaPct, deltaAbs });
        } catch (prevError) {
          if (fetchSeqRef.current !== seq) return;
          console.error('Errore caricamento confronto mese precedente:', prevError);
          setComparisonStats({
            previousTotal: 0,
            deltaPct: 0,
            deltaAbs: 0
          });
        }
      })();

      void (async () => {
        const [distributionResult, trendResult, yearComparisonResult] = await Promise.allSettled([
          api.get('/dossier/commerciali-chiusure/distribution', { params: baseFiltersParams, timeout: 20000 }),
          api.get('/dossier/commerciali-chiusure/trend-monthly', {
            params: {
              ...baseFiltersParams,
              trendMonth
            },
            timeout: 20000
          }),
          api.get('/dossier/commerciali-chiusure/year-comparison', {
            params: {
              ...baseFiltersParams,
              referenceYear
            },
            timeout: 20000
          })
        ]);

        if (fetchSeqRef.current !== seq) return;

        if (distributionResult.status === 'fulfilled') {
          setCommercialeDistribution(
            Array.isArray(distributionResult.value.data?.distribution)
              ? distributionResult.value.data.distribution
              : []
          );
        } else {
          console.error('Errore caricamento distribuzione commerciale:', distributionResult.reason);
          setCommercialeDistribution([]);
        }

        if (trendResult.status === 'fulfilled') {
          setMonthlyTrend({
            trendMonth: String(trendResult.value.data?.trendMonth || trendMonth),
            data: Array.isArray(trendResult.value.data?.data) ? trendResult.value.data.data : []
          });
        } else {
          console.error('Errore caricamento trend mensile:', trendResult.reason);
          setMonthlyTrend({ trendMonth, data: [] });
        }

        if (yearComparisonResult.status === 'fulfilled') {
          setYearComparison({
            currentYear: Number(yearComparisonResult.value.data?.currentYear || referenceYear),
            previousYear: Number(yearComparisonResult.value.data?.previousYear || (referenceYear - 1)),
            data: Array.isArray(yearComparisonResult.value.data?.data)
              ? yearComparisonResult.value.data.data
              : []
          });
        } else {
          console.error('Errore caricamento confronto annuale:', yearComparisonResult.reason);
          setYearComparison({
            currentYear: referenceYear,
            previousYear: referenceYear - 1,
            data: []
          });
        }
      })();
    } catch (error) {
      if (fetchSeqRef.current !== seq) return;
      console.error('Errore caricamento commerciali chiusure:', error);
      setRows([]);
      setTotal(0);
      setStats({
        totaleContratti: 0,
        importoTotale: 0,
        mediaImporto: 0
      });
      setComparisonStats({
        previousTotal: 0,
        deltaPct: 0,
        deltaAbs: 0
      });
      setCommercialeDistribution([]);
      setMonthlyTrend({ trendMonth: resolveTrendMonth(filtersArg), data: [] });
      setYearComparison({
        currentYear: new Date().getFullYear(),
        previousYear: new Date().getFullYear() - 1,
        data: []
      });
    } finally {
      if (fetchSeqRef.current === seq) setLoading(false);
    }
  };

  useEffect(() => {
    const loadedDefaults = loadDefaultFilters();
    setFilters(loadedDefaults);
    setAppliedFilters(loadedDefaults);
    setSearchInput(loadedDefaults.search || '');
    fetchFilterOptions();
    fetchRows(0, rowsPerPage, orderBy, order, loadedDefaults);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchInput === filters.search) return;
      const next = { ...filters, search: searchInput };
      setFilters(next);
      setAppliedFilters(next);
      setPage(0);
      fetchRows(0, rowsPerPage, orderBy, order, next);
    }, 350);
    return () => clearTimeout(timer);
  }, [searchInput, filters, rowsPerPage, orderBy, order]);

  const handleChangePage = (_event, nextPage) => {
    setPage(nextPage);
    fetchRows(nextPage, rowsPerPage, orderBy, order, appliedFilters);
  };

  const handleChangeRowsPerPage = (event) => {
    const nextRows = parseInt(event.target.value, 10) || 25;
    setRowsPerPage(nextRows);
    setPage(0);
    fetchRows(0, nextRows, orderBy, order, appliedFilters);
  };

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    const nextOrder = isAsc ? 'desc' : 'asc';
    setOrder(nextOrder);
    setOrderBy(property);
    setPage(0);
    fetchRows(0, rowsPerPage, property, nextOrder, appliedFilters);
  };

  const applyFilters = (nextFilters) => {
    setFilters(nextFilters);
    setAppliedFilters(nextFilters);
    setPage(0);
    fetchRows(0, rowsPerPage, orderBy, order, nextFilters);
  };

  const activeFilters = useMemo(() => {
    const chips = [];
    const tipoContrattoValues = toArray(appliedFilters.tipoContratto);
    if (tipoContrattoValues.length > 0) {
      chips.push({ key: 'tipoContratto', label: `Tipo Contratto: ${tipoContrattoValues.join(', ')}` });
    }
    const statoPreventivoValues = toArray(appliedFilters.statoPreventivo);
    if (statoPreventivoValues.length > 0) {
      chips.push({ key: 'statoPreventivo', label: `Stato Preventivo: ${statoPreventivoValues.join(', ')}` });
    }
    const statoContrattoValues = toArray(appliedFilters.statoContratto);
    if (statoContrattoValues.length > 0) {
      chips.push({ key: 'statoContratto', label: `Stato Contratto: ${statoContrattoValues.join(', ')}` });
    }
    if (appliedFilters.respComm) {
      chips.push({ key: 'respComm', label: `Resp. commerciale: ${appliedFilters.respComm}` });
    }
    if (appliedFilters.respUpsell) {
      chips.push({ key: 'respUpsell', label: `Resp. upsell: ${appliedFilters.respUpsell}` });
    }
    if (appliedFilters.statoDocumenti) {
      chips.push({ 
        key: 'statoDocumenti', 
        label: `Stato Documenti: ${
          appliedFilters.statoDocumenti === 'completi'
            ? 'Completi'
            : appliedFilters.statoDocumenti === 'parziali'
              ? 'Caricati parzialmente'
              : 'Mancanti'
        }` 
      });
    }
    if (appliedFilters.dataConferimentoFrom) {
      chips.push({
        key: 'dataConferimentoFrom',
        label: `data Conferimento dal : ${formatDate(appliedFilters.dataConferimentoFrom)}`
      });
    }
    if (appliedFilters.dataConferimentoTo) {
      chips.push({
        key: 'dataConferimentoTo',
        label: `al: ${formatDate(appliedFilters.dataConferimentoTo)}`
      });
    }
    if (appliedFilters.dataInvioDocumentiFrom) {
      chips.push({
        key: 'dataInvioDocumentiFrom',
        label: `Invio doc. dal: ${formatDate(appliedFilters.dataInvioDocumentiFrom)}`
      });
    }
    if (appliedFilters.dataInvioDocumentiTo) {
      chips.push({
        key: 'dataInvioDocumentiTo',
        label: `Invio doc. al: ${formatDate(appliedFilters.dataInvioDocumentiTo)}`
      });
    }
    if (appliedFilters.search) {
      chips.push({ key: 'search', label: `Ricerca: ${appliedFilters.search}` });
    }
    return chips;
  }, [appliedFilters]);

  const handleDeleteActiveFilter = (key) => {
    const nextValue = ['tipoContratto', 'statoPreventivo', 'statoContratto'].includes(key) ? [] : '';
    const next = { ...appliedFilters, [key]: nextValue };
    setFilters(next);
    setAppliedFilters(next);
    if (key === 'search') setSearchInput('');
    setPage(0);
    fetchRows(0, rowsPerPage, orderBy, order, next);
  };

  const handleOpenColumnMenu = (event) => {
    setColumnMenuAnchorEl(event.currentTarget);
  };

  const handleCloseColumnMenu = () => {
    setColumnMenuAnchorEl(null);
  };

  const handleToggleColumn = (key) => {
    const prevVisibility = columnVisibility;
    const nextVisibility = {
      ...prevVisibility,
      [key]: !prevVisibility[key]
    };

    const prevIncludeFinancials = resolveIncludeFinancials(prevVisibility);
    const nextIncludeFinancials = resolveIncludeFinancials(nextVisibility);

    setColumnVisibility(nextVisibility);

    if (!prevIncludeFinancials && nextIncludeFinancials) {
      setPage(0);
      fetchRows(0, rowsPerPage, orderBy, order, appliedFilters, nextVisibility);
    }
  };

  const fetchRowNegotiations = async (dossierId) => {
    setRowNegotiationsLoading((prev) => ({ ...prev, [dossierId]: true }));
    try {
      const res = await api.get(`/negotiation/dossier/${dossierId}`);
      setRowNegotiations((prev) => ({
        ...prev,
        [dossierId]: Array.isArray(res.data) ? res.data : []
      }));
    } catch (error) {
      console.error('Errore caricamento pratiche collegate:', error);
      setRowNegotiations((prev) => ({
        ...prev,
        [dossierId]: []
      }));
    } finally {
      setRowNegotiationsLoading((prev) => ({ ...prev, [dossierId]: false }));
    }
  };

  const handleToggleRowDetails = (dossierId) => {
    setExpandedRows((prev) => {
      if (prev[dossierId]) return {};
      return { [dossierId]: true };
    });
    if (!rowNegotiations[dossierId] && !rowNegotiationsLoading[dossierId]) {
      fetchRowNegotiations(dossierId);
    }
  };

  useEffect(() => {
    if (!richiestaDocDialogOpen) return undefined;
    const dossierId = Number(richiestaDocTarget?.id);
    if (!Number.isInteger(dossierId) || dossierId <= 0) return undefined;

    let alive = true;
    setRichiestaDocExistingLoading(true);
    api.get(`/dossier/${dossierId}/richieste-doc`)
      .then((res) => {
        if (!alive) return;
        setRichiestaDocExisting(res?.data?.richiesta || null);
      })
      .catch((error) => {
        if (!alive) return;
        console.error('Errore caricamento richiesta documenti:', error);
        setRichiestaDocExisting(null);
      })
      .finally(() => {
        if (!alive) return;
        setRichiestaDocExistingLoading(false);
      });

    return () => {
      alive = false;
    };
  }, [richiestaDocDialogOpen, richiestaDocTarget?.id]);

  const resetRichiestaDocDialog = () => {
    setRichiestaDocFiles({
      contratto: null,
      mandato: null,
      doc_identita: null,
      visura: null,
      questionario: null
    });
    setRichiestaDocExisting(null);
    setRichiestaDocExistingLoading(false);
    setRichiestaDocDuplicates({});
    setRichiestaDocUnmatched([]);
    if (richiestaDocFileRef.current) richiestaDocFileRef.current.value = '';
    if (richiestaDocPreviewUrl) {
      window.URL.revokeObjectURL(richiestaDocPreviewUrl);
      setRichiestaDocPreviewUrl('');
    }
    setRichiestaDocPreviewLoading(false);
    setRichiestaDocPreviewRow(null);
    setRichiestaDocPreviewOpen(false);
  };

  const openRichiestaDocDialog = (row) => {
    setRichiestaDocTarget(row || null);
    resetRichiestaDocDialog();
    setRichiestaDocDialogOpen(true);
  };

  const closeRichiestaDocDialog = () => {
    if (richiestaDocSaving) return;
    setRichiestaDocDialogOpen(false);
    setRichiestaDocTarget(null);
    resetRichiestaDocDialog();
  };

  const handlePickRichiestaDocFiles = () => {
    if (richiestaDocSaving) return;
    richiestaDocFileRef.current?.click?.();
  };

  const handleRichiestaDocFilesSelected = (event) => {
    const picked = Array.from(event.target.files || []);
    event.target.value = '';
    if (picked.length === 0) return;

    const nextFiles = { ...(richiestaDocFiles || {}) };
    const nextDup = { ...(richiestaDocDuplicates || {}) };
    const nextUnmatched = [...(richiestaDocUnmatched || [])];

    picked.forEach((file) => {
      const type = detectRichiestaDocTypeFromName(file?.name);
      if (!type) {
        nextUnmatched.push(file);
        return;
      }
      if (type === 'visura' && !richiestaDocIsAzienda) {
        nextUnmatched.push(file);
        return;
      }
      if (!nextFiles[type]) {
        nextFiles[type] = file;
        return;
      }
      nextDup[type] = [...(nextDup[type] || []), file];
    });

    setRichiestaDocFiles(nextFiles);
    setRichiestaDocDuplicates(nextDup);
    setRichiestaDocUnmatched(nextUnmatched);
  };

  const removeRichiestaDocFile = (type) => {
    setRichiestaDocFiles((prev) => ({ ...(prev || {}), [type]: null }));
    if (richiestaDocPreviewOpen && richiestaDocPreviewRow?.type === type && richiestaDocPreviewRow?.source === 'local') {
      setRichiestaDocPreviewOpen(false);
      setRichiestaDocPreviewRow(null);
      if (richiestaDocPreviewUrl) {
        window.URL.revokeObjectURL(richiestaDocPreviewUrl);
        setRichiestaDocPreviewUrl('');
      }
    }
  };

  const refreshRichiestaDocExisting = async () => {
    const dossierId = Number(richiestaDocTarget?.id);
    if (!Number.isInteger(dossierId) || dossierId <= 0) return;
    setRichiestaDocExistingLoading(true);
    try {
      const res = await api.get(`/dossier/${dossierId}/richieste-doc`);
      setRichiestaDocExisting(res?.data?.richiesta || null);
    } catch (error) {
      console.error('Errore caricamento richiesta documenti:', error);
      setRichiestaDocExisting(null);
    } finally {
      setRichiestaDocExistingLoading(false);
    }
  };

  const deleteRichiestaDocFile = async (type) => {
    if (richiestaDocSaving || richiestaDocDeleting) return;
    const dossierId = Number(richiestaDocTarget?.id);
    if (!Number.isInteger(dossierId) || dossierId <= 0) {
      toast.error('Dossier non valido');
      return;
    }
    try {
      setRichiestaDocDeleting(true);
      await api.delete(`/dossier/${dossierId}/richieste-doc/file/${type}`);
      toast.success('File eliminato');
      if (richiestaDocPreviewOpen && richiestaDocPreviewRow?.type === type) {
        setRichiestaDocPreviewOpen(false);
        setRichiestaDocPreviewRow(null);
        if (richiestaDocPreviewUrl) {
          window.URL.revokeObjectURL(richiestaDocPreviewUrl);
          setRichiestaDocPreviewUrl('');
        }
      }
      await refreshRichiestaDocExisting();
      fetchRows(page, rowsPerPage, orderBy, order, appliedFilters, columnVisibility);
    } catch (error) {
      toast.error(error.response?.data?.message || error.message || 'Errore eliminazione file');
    } finally {
      setRichiestaDocDeleting(false);
    }
  };

  const handleCloseRichiestaDocPreview = () => {
    setRichiestaDocPreviewOpen(false);
    setRichiestaDocPreviewRow(null);
    if (richiestaDocPreviewUrl) {
      window.URL.revokeObjectURL(richiestaDocPreviewUrl);
      setRichiestaDocPreviewUrl('');
    }
  };

  const handleOpenRichiestaDocPreview = async (type) => {
    const dossierId = Number(richiestaDocTarget?.id);
    if (!Number.isInteger(dossierId) || dossierId <= 0) {
      toast.error('Dossier non valido');
      return;
    }

    const localFile = richiestaDocFiles?.[type] || null;
    const existingName =
      type === 'contratto'
        ? richiestaDocExisting?.contratto_file_name
        : type === 'mandato'
          ? richiestaDocExisting?.mandato_file_name
          : type === 'doc_identita'
            ? richiestaDocExisting?.doc_identita_file_name
            : type === 'visura'
              ? richiestaDocExisting?.visura_file_name
              : richiestaDocExisting?.questionario_file_name;
    const existingSize =
      type === 'contratto'
        ? richiestaDocExisting?.contratto_file_size
        : type === 'mandato'
          ? richiestaDocExisting?.mandato_file_size
          : type === 'doc_identita'
            ? richiestaDocExisting?.doc_identita_file_size
            : type === 'visura'
              ? richiestaDocExisting?.visura_file_size
              : richiestaDocExisting?.questionario_file_size;

    if (!localFile && !existingName) {
      toast.warning('Nessun file da visualizzare');
      return;
    }

    if (richiestaDocPreviewUrl) {
      window.URL.revokeObjectURL(richiestaDocPreviewUrl);
      setRichiestaDocPreviewUrl('');
    }

    const name = localFile?.name || existingName || type;
    const mimeType = localFile?.type || guessMimeFromFilename(name);
    setRichiestaDocPreviewRow({
      type,
      name,
      mimeType,
      sizeBytes: localFile?.size != null ? Number(localFile.size) : Number(existingSize || 0),
      source: localFile ? 'local' : 'existing'
    });
    setRichiestaDocPreviewOpen(true);

    const mime = String(mimeType || '').toLowerCase();
    const isPreviewable = mime.includes('pdf') || mime.startsWith('image/');
    if (!isPreviewable) {
      toast.warning("Formato file non supportato per l'anteprima");
      return;
    }

    try {
      setRichiestaDocPreviewLoading(true);
      if (localFile) {
        const url = window.URL.createObjectURL(localFile);
        setRichiestaDocPreviewUrl(url);
        return;
      }

      const res = await api.get(`/dossier/${dossierId}/richieste-doc/file/${type}/download`, {
        responseType: 'blob'
      });
      const blob = new Blob([res.data], { type: mimeType || 'application/octet-stream' });
      const url = window.URL.createObjectURL(blob);
      setRichiestaDocPreviewUrl(url);
    } catch (error) {
      toast.error(error.response?.data?.message || error.message || "Errore durante il caricamento dell'anteprima");
    } finally {
      setRichiestaDocPreviewLoading(false);
    }
  };

  const handleOpenRichiestaDocPreviewInNewTab = async () => {
    if (!richiestaDocPreviewRow) return;
    const dossierId = Number(richiestaDocTarget?.id);
    if (!Number.isInteger(dossierId) || dossierId <= 0) return;

    if (richiestaDocPreviewUrl) {
      window.open(richiestaDocPreviewUrl, '_blank', 'noopener,noreferrer');
      return;
    }

    if (richiestaDocPreviewRow.source === 'local') return;

    try {
      setRichiestaDocPreviewOpenInNewTabLoading(true);
      const res = await api.get(`/dossier/${dossierId}/richieste-doc/file/${richiestaDocPreviewRow.type}/download`, {
        responseType: 'blob'
      });
      const blob = new Blob([res.data], { type: richiestaDocPreviewRow.mimeType || 'application/octet-stream' });
      const url = window.URL.createObjectURL(blob);
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (error) {
      toast.error(error.response?.data?.message || error.message || "Errore durante l'apertura del documento");
    } finally {
      setRichiestaDocPreviewOpenInNewTabLoading(false);
    }
  };

  const openDeleteRichiestaConfirm = () => {
    if (richiestaDocSaving || richiestaDocDeleting) return;
    setRichiestaDocDeleteConfirmOpen(true);
  };

  const deleteRichiestaDoc = async () => {
    if (richiestaDocSaving || richiestaDocDeleting) return;
    const dossierId = Number(richiestaDocTarget?.id);
    if (!Number.isInteger(dossierId) || dossierId <= 0) {
      toast.error('Dossier non valido');
      return;
    }
    try {
      setRichiestaDocDeleting(true);
      await api.delete(`/dossier/${dossierId}/richieste-doc`);
      toast.success('Richiesta eliminata');
      setRichiestaDocDeleteConfirmOpen(false);
      closeRichiestaDocDialog();
      fetchRows(page, rowsPerPage, orderBy, order, appliedFilters, columnVisibility);
    } catch (error) {
      toast.error(error.response?.data?.message || error.message || 'Errore eliminazione richiesta');
    } finally {
      setRichiestaDocDeleting(false);
    }
  };

  const submitRichiestaDoc = async () => {
    if (richiestaDocSaving) return;
    const dossierId = Number(richiestaDocTarget?.id);
    if (!Number.isInteger(dossierId) || dossierId <= 0) {
      toast.error('Dossier non valido');
      return;
    }
    const files = richiestaDocFiles || {};
    const existing = richiestaDocExisting;
    const hasExistingRequest = Boolean(existing?.id || existing?.is_draft);
    const hasAnyNew = richiestaDocAllTypes.some((type) => Boolean(files[type]));

    if (!hasAnyNew && hasExistingRequest) {
      closeRichiestaDocDialog();
      return;
    }
    if (!hasAnyNew) {
      toast.error(
        richiestaDocIsAzienda
          ? 'Seleziona almeno un file riconosciuto (contratto / mandato / doc id / visura / questionario)'
          : 'Seleziona almeno un file riconosciuto (contratto / mandato / doc id / questionario)'
      );
      return;
    }

    try {
      setRichiestaDocSaving(true);
      const form = new FormData();
      richiestaDocAllTypes.forEach((type) => {
        const file = files[type];
        if (!file) return;
        form.append('files', file);
        form.append('types', type);
      });

      const res = await api.post(`/dossier/${dossierId}/richieste-doc`, form, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      if (res?.data?.draft) {
        toast.success('Bozza salvata');
      } else {
        const issuerCount = Number(res?.data?.issuer_count || 0);
        toast.success(`Richiesta documenti creata (${issuerCount} creditori)`);
      }
      setRichiestaDocDialogOpen(false);
      setRichiestaDocTarget(null);
      resetRichiestaDocDialog();
      fetchRows(page, rowsPerPage, orderBy, order, appliedFilters, columnVisibility);
    } catch (error) {
      toast.error(error.response?.data?.message || error.message || 'Errore creazione richiesta documenti');
    } finally {
      setRichiestaDocSaving(false);
    }
  };

  const handleStartFirmeReport = async () => {
    try {
      if (reportLoading) return;
      setReportLoading(true);
      const payload = {
        filters: {
          search: appliedFilters.search || '',
          tipoContratto: toArray(appliedFilters.tipoContratto),
          statoPreventivo: toArray(appliedFilters.statoPreventivo),
          statoContratto: toArray(appliedFilters.statoContratto),
          respComm: appliedFilters.respComm || '',
          respUpsell: appliedFilters.respUpsell || '',
          dataConferimentoFrom: appliedFilters.dataConferimentoFrom || '',
          dataConferimentoTo: appliedFilters.dataConferimentoTo || '',
          dataInvioDocumentiFrom: appliedFilters.dataInvioDocumentiFrom || '',
          dataInvioDocumentiTo: appliedFilters.dataInvioDocumentiTo || ''
        },
        includeDettaglioPratiche: includeReportDetail
      };
      const res = await api.post('/reports/firme', payload);
      const jobId = res?.data?.jobId;
      if (!jobId) {
        throw new Error('jobId non ricevuto');
      }
      addJob(jobId, 'firme_report');
      setReportDialogOpen(false);
    } catch (error) {
      console.error('Errore avvio report firme:', error);
      toast.error("Errore durante l'avvio del Report Firme");
    } finally {
      setReportLoading(false);
    }
  };

  const handleExportCsv = async () => {
    if (csvLoading) return;
    setCsvLoading(true);
    try {
      const payload = {
        filters: {
          search: appliedFilters.search || '',
          tipoContratto: toArray(appliedFilters.tipoContratto),
          statoPreventivo: toArray(appliedFilters.statoPreventivo),
          statoContratto: toArray(appliedFilters.statoContratto),
          respComm: appliedFilters.respComm || '',
          respUpsell: appliedFilters.respUpsell || '',
          dataConferimentoFrom: appliedFilters.dataConferimentoFrom || '',
          dataConferimentoTo: appliedFilters.dataConferimentoTo || '',
          dataInvioDocumentiFrom: appliedFilters.dataInvioDocumentiFrom || '',
          dataInvioDocumentiTo: appliedFilters.dataInvioDocumentiTo || ''
        }
      };
      const res = await api.post('/reports/firme-csv', payload);
      const jobId = res?.data?.jobId;
      if (!jobId) {
        throw new Error('jobId non ricevuto');
      }
      addJob(jobId, 'firme_csv_report');
    } catch (error) {
      console.error('Errore esportazione CSV commerciali chiusure:', error);
      toast.error("Errore durante l'avvio dell'esportazione CSV");
    } finally {
      setCsvLoading(false);
    }
  };

  const handleValidateFirme = () => {
    if (firmeValidationLoading) return;
    setFirmeValidationConfirmOpen(true);
  };

  const startFirmeValidation = async () => {
    if (firmeValidationLoading) return;
    setFirmeValidationLoading(true);
    try {
      setFirmeValidationMap({});
      setFirmeValidationResults([]);
      setFirmeValidationSummary(null);
      const payload = {
        filters: {
          search: appliedFilters.search || '',
          tipoContratto: toArray(appliedFilters.tipoContratto),
          statoPreventivo: toArray(appliedFilters.statoPreventivo),
          statoContratto: toArray(appliedFilters.statoContratto),
          respComm: appliedFilters.respComm || '',
          respUpsell: appliedFilters.respUpsell || '',
          dataConferimentoFrom: appliedFilters.dataConferimentoFrom || '',
          dataConferimentoTo: appliedFilters.dataConferimentoTo || '',
          dataInvioDocumentiFrom: appliedFilters.dataInvioDocumentiFrom || '',
          dataInvioDocumentiTo: appliedFilters.dataInvioDocumentiTo || ''
        }
      };
      const res = await api.post('/dossier/commerciali-chiusure/validate-firme', payload);
      const data = Array.isArray(res?.data?.data) ? res.data.data : [];
      const summary = res?.data?.summary || null;

      const nextMap = {};
      data.forEach((item) => {
        if (item && item.dossierId != null) nextMap[item.dossierId] = item;
      });

      setFirmeValidationMap(nextMap);
      setFirmeValidationResults(data);
      setFirmeValidationSummary(summary);
      setFirmeValidationConfirmOpen(false);
      setFirmeValidationDialogOpen(true);
      if (summary && typeof summary.found !== 'undefined' && typeof summary.total !== 'undefined') {
        toast.success(`Convalida completata: ${summary.found}/${summary.total} trovati`);
      } else {
        toast.success('Convalida completata');
      }
    } catch (error) {
      const message = error?.response?.data?.message;
      console.error('Errore convalida firme:', error);
      toast.error(message || 'Errore durante la convalida firme');
    } finally {
      setFirmeValidationLoading(false);
    }
  };

  const visibleColumnsCount = Object.values(columnVisibility).filter(Boolean).length;
  const totalColumns = visibleColumnsCount + 2;
  const twoLineHeader = (top, bottom) => (
    <Box component="span" sx={{ display: 'inline-flex', flexDirection: 'column', lineHeight: 1.05 }}>
      <span>{top}</span>
      <span>{bottom}</span>
    </Box>
  );
  const commercialeDistributionChartData = [...commercialeDistribution]
    .map((row) => ({
      name: row.name || 'Non assegnato',
      value: Number(row.total || 0)
    }))
    .filter((row) => row.value > 0)
    .sort((a, b) => b.value - a.value);

  const distributionChartHeight = Math.min(
    Math.max(280, commercialeDistributionChartData.length * 30 + 80),
    900
  );
  const distributionYAxisWidth = Math.min(
    Math.max(
      160,
      commercialeDistributionChartData.reduce(
        (max, row) => Math.max(max, String(row.name || '').length),
        0
      ) * 7
    ),
    280
  );
  const distributionLeftMargin = Math.max(120, distributionYAxisWidth + 24);
  const trendPointMap = new Map(
    (monthlyTrend.data || [])
      .map((item) => ({
        day: normalizeTrendDayKey(item?.day),
        total: Number(item?.total || 0)
      }))
      .filter((item) => item.day)
      .map((item) => [item.day, item.total])
  );
  const trendMonthStr = String(monthlyTrend.trendMonth || '');
  const trendYear = Number((trendMonthStr.split('-')[0] || 0));
  const trendMonthNum = Number((trendMonthStr.split('-')[1] || 0));
  const hasValidTrendMonth =
    Number.isInteger(trendYear) &&
    Number.isInteger(trendMonthNum) &&
    trendYear > 0 &&
    trendMonthNum >= 1 &&
    trendMonthNum <= 12;
  const trendDaysInMonth = hasValidTrendMonth
    ? new Date(trendYear, trendMonthNum, 0).getDate()
    : 0;
  const monthlyTrendChartData = hasValidTrendMonth
    ? Array.from({ length: trendDaysInMonth }, (_unused, idx) => {
        const day = idx + 1;
        const key = `${trendYear}-${String(trendMonthNum).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
        return {
          dayLabel: String(day).padStart(2, '0'),
          value: trendPointMap.get(key) || 0
        };
      })
    : [];
  const monthlyTrendAverage =
    monthlyTrendChartData.length > 0
      ? monthlyTrendChartData.reduce((sum, item) => sum + Number(item.value || 0), 0) /
        monthlyTrendChartData.length
      : 0;
  const monthlyTrendChartDataWithAvg = monthlyTrendChartData.map((item) => ({
    ...item,
    avg: monthlyTrendAverage
  }));
  const trendMonthLabel = hasValidTrendMonth
    ? new Date(trendYear, trendMonthNum - 1, 1).toLocaleDateString('it-IT', {
        month: 'long',
        year: 'numeric'
      })
    : '';
  const monthLabelsShort = ['Gen', 'Feb', 'Mar', 'Apr', 'Mag', 'Giu', 'Lug', 'Ago', 'Set', 'Ott', 'Nov', 'Dic'];
  const yearComparisonChartData = Array.from({ length: 12 }, (_unused, idx) => {
    const month = idx + 1;
    const row = (yearComparison.data || []).find((item) => Number(item.month) === month);
    return {
      monthLabel: monthLabelsShort[idx],
      currentYearTotal: Number(row?.currentYearTotal || 0),
      previousYearTotal: Number(row?.previousYearTotal || 0)
    };
  });

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box
            sx={(theme) => ({
              width: 40,
              height: 40,
              borderRadius: '50%',
              bgcolor: theme.palette.mode === 'dark' ? alpha('#6366f1', 0.2) : '#eef2ff',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            })}
          >
            <FileText size={20} color="#4f46e5" />
          </Box>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 800, color: 'text.primary', letterSpacing: '-0.01em' }}>
              Commerciali (Firme)
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '1rem' }}>
              Elenco dossier con dati commerciali e avanzamento firme
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Button
            variant="outlined"
            color="inherit"
            startIcon={<FileDown size={16} />}
            onClick={handleExportCsv}
            disabled={loading || csvLoading}
            sx={{ textTransform: 'none', fontWeight: 700 }}
          >
            {csvLoading ? 'Avvio...' : 'Esporta in CSV'}
          </Button>
          <Button
            variant="contained"
            color="primary"
            startIcon={<FileText size={16} />}
            onClick={() => {
              setIncludeReportDetail(true);
              setReportDialogOpen(true);
            }}
            sx={{ textTransform: 'none', fontWeight: 700 }}
          >
            Report Firme
          </Button>
          <Button
            variant="outlined"
            color="inherit"
            startIcon={firmeValidationLoading ? <CircularProgress size={16} /> : <CheckCircle2 size={16} />}
            onClick={handleValidateFirme}
            disabled={loading || firmeValidationLoading}
            sx={{ textTransform: 'none', fontWeight: 700 }}
          >
            Convalida Firme
          </Button>
        </Box>
      </Box>

      <StyledCard>
        <Box sx={{ p: 2.5, pb: 1 }}>
          <Grid container spacing={1.5}>
            <Grid item xs={12} md={3}>
              <RecallStatWidget
                title="Totale contratti"
                value={stats.totaleContratti.toLocaleString('it-IT')}
                icon={FileText}
                color="#334155"
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <RecallStatWidget
                title="Importo totale"
                value={formatMoney(stats.importoTotale)}
                icon={WalletCards}
                color="#059669"
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <RecallStatWidget
                title="Media"
                value={formatMoney(stats.mediaImporto)}
                icon={Calculator}
                color="#0284c7"
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <Paper
                elevation={0}
                variant="outlined"
                sx={(theme) => ({
                  p: 2,
                  display: 'flex',
                  alignItems: 'center',
                  height: '100%',
                  borderColor:
                    theme.palette.mode === 'dark'
                      ? alpha(theme.palette.common.white, 0.14)
                      : '#e2e8f0',
                  borderRadius: 2,
                  bgcolor:
                    theme.palette.mode === 'dark'
                      ? alpha(theme.palette.background.paper, 0.78)
                      : '#ffffff'
                })}
              >
                <Box
                  sx={(theme) => ({
                    p: 1.5,
                    borderRadius: '50%',
                    bgcolor:
                      comparisonStats.deltaAbs >= 0
                        ? alpha(theme.palette.success.main, 0.14)
                        : alpha(theme.palette.error.main, 0.14),
                    color: comparisonStats.deltaAbs >= 0 ? 'success.main' : 'error.main',
                    mr: 2,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  })}
                >
                  {comparisonStats.deltaAbs >= 0 ? <TrendingUp size={24} /> : <TrendingDown size={24} />}
                </Box>
                <Box>
                  <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                    Vs mese precedente
                  </Typography>
                  <Typography
                    variant="h6"
                    sx={{ fontWeight: 'bold', color: comparisonStats.deltaAbs >= 0 ? 'success.main' : 'error.main', lineHeight: 1.2 }}
                  >
                    {`${comparisonStats.deltaPct >= 0 ? '+' : ''}${comparisonStats.deltaPct.toFixed(1)}%`}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {`${comparisonStats.deltaAbs >= 0 ? '+' : ''}${comparisonStats.deltaAbs} contratti (mese prec: ${comparisonStats.previousTotal})`}
                  </Typography>
                </Box>
              </Paper>
            </Grid>
          </Grid>
        </Box>

        <Box
          sx={{
            px: 3,
            py: 2,
            borderBottom: '1px solid',
            borderColor: 'divider',
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            justifyContent: 'space-between',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <TextField
              size="small"
              placeholder="Cerca per cliente, responsabile, stato..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search size={18} color="currentColor" />
                  </InputAdornment>
                )
              }}
              sx={(theme) => ({
                minWidth: 360,
                '& .MuiInputAdornment-root': { color: theme.palette.text.secondary },
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2.5,
                  bgcolor: theme.palette.mode === 'dark' ? alpha(theme.palette.background.default, 0.42) : '#ffffff',
                  fontSize: '1rem',
                  color: theme.palette.text.primary,
                  '& fieldset': { borderColor: theme.palette.divider },
                  '&:hover fieldset': { borderColor: theme.palette.text.secondary },
                  '&.Mui-focused fieldset': { borderColor: theme.palette.primary.main }
                }
              })}
            />
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Tooltip title="Mostra filtri avanzati">
              <IconButton size="small" onClick={() => setShowFilters((prev) => !prev)} sx={{ color: 'text.secondary' }}>
                <FilterAltIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Seleziona colonne">
              <IconButton size="small" onClick={handleOpenColumnMenu} sx={{ color: 'text.secondary' }}>
                <ViewColumnIcon fontSize="small" color={visibleColumnsCount < 12 ? 'primary' : 'action'} />
              </IconButton>
            </Tooltip>
          </Box>
          <Menu
            anchorEl={columnMenuAnchorEl}
            open={Boolean(columnMenuAnchorEl)}
            onClose={handleCloseColumnMenu}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          >
            <Box sx={{ px: 2, pt: 1 }}>
              <Typography variant="caption" color="text.secondary">
                Colonne visibili
              </Typography>
            </Box>
            <FormGroup sx={{ px: 2, pb: 1 }}>
                <FormControlLabel control={<Checkbox checked={columnVisibility.responsabileCommerciale} onChange={() => handleToggleColumn('responsabileCommerciale')} />} label="Resp. commerciale" />
                <FormControlLabel control={<Checkbox checked={columnVisibility.responsabileUpsell} onChange={() => handleToggleColumn('responsabileUpsell')} />} label="Resp. upsell" />
                <FormControlLabel control={<Checkbox checked={columnVisibility.customer} onChange={() => handleToggleColumn('customer')} />} label="Cliente" />
                <FormControlLabel control={<Checkbox checked={columnVisibility.tipoContratto} onChange={() => handleToggleColumn('tipoContratto')} />} label="Tipo contratto" />
                <FormControlLabel control={<Checkbox checked={columnVisibility.statoPreventivo} onChange={() => handleToggleColumn('statoPreventivo')} />} label="Stato preventivo" />
                <FormControlLabel control={<Checkbox checked={columnVisibility.dataConferimento} onChange={() => handleToggleColumn('dataConferimento')} />} label="Data conferimento" />
                <FormControlLabel control={<Checkbox checked={columnVisibility.importoTotale} onChange={() => handleToggleColumn('importoTotale')} />} label="Importo totale" />
                <FormControlLabel control={<Checkbox checked={columnVisibility.contrattiImporto} onChange={() => handleToggleColumn('contrattiImporto')} />} label="Contratti" />
                <FormControlLabel control={<Checkbox checked={columnVisibility.upsellImporto} onChange={() => handleToggleColumn('upsellImporto')} />} label="Upsell" />
                <FormControlLabel control={<Checkbox checked={columnVisibility.incassatoImporto} onChange={() => handleToggleColumn('incassatoImporto')} />} label="Incassato" />
                <FormControlLabel control={<Checkbox checked={columnVisibility.incassatoPercent} onChange={() => handleToggleColumn('incassatoPercent')} />} label="% sul totale" />
                <FormControlLabel control={<Checkbox checked={columnVisibility.saldoDaIncassare} onChange={() => handleToggleColumn('saldoDaIncassare')} />} label="Saldo" />
                <FormControlLabel control={<Checkbox checked={columnVisibility.acconto} onChange={() => handleToggleColumn('acconto')} />} label="Acconto" />
                <FormControlLabel control={<Checkbox checked={columnVisibility.percentageTransition} onChange={() => handleToggleColumn('percentageTransition')} />} label="% transition" />
                <FormControlLabel control={<Checkbox checked={columnVisibility.dataInvioDocumenti} onChange={() => handleToggleColumn('dataInvioDocumenti')} />} label="Data invio documenti" />
                <FormControlLabel control={<Checkbox checked={columnVisibility.dataChiusuraConferimento} onChange={() => handleToggleColumn('dataChiusuraConferimento')} />} label="Data chiusura conferimento" />
                <FormControlLabel control={<Checkbox checked={columnVisibility.funnelixConvalida} onChange={() => handleToggleColumn('funnelixConvalida')} />} label="Convalida Funnelix" />
            </FormGroup>
          </Menu>
        </Box>

        {showFilters && (
          <Box
            sx={{
              px: 3,
              py: 2,
              borderBottom: '1px solid',
              borderColor: 'divider',
            }}
          >
          <Grid container spacing={1.5} alignItems="center">
            <Grid item xs={12} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Tipo contratto</InputLabel>
                <Select
                  multiple
                  value={filters.tipoContratto}
                  label="Tipo contratto"
                  onChange={(e) => applyFilters({ ...filters, tipoContratto: e.target.value })}
                  renderValue={(selected) => {
                    const values = toArray(selected);
                    if (values.length === 0) return 'Tutti';
                    return values.join(', ');
                  }}
                >
                  {contractTypeOptions.map((item) => (
                    <MenuItem key={item} value={item}>
                      <Checkbox checked={toArray(filters.tipoContratto).includes(item)} size="small" />
                      {item}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Stato Preventivo</InputLabel>
                <Select
                  multiple
                  value={filters.statoPreventivo}
                  label="Stato Preventivo"
                  onChange={(e) => applyFilters({ ...filters, statoPreventivo: e.target.value })}
                  renderValue={(selected) => {
                    const values = toArray(selected);
                    if (values.length === 0) return 'Tutti';
                    return values.join(', ');
                  }}
                >
                  {statoPreventivoOptions.map((item) => (
                    <MenuItem key={item} value={item}>
                      <Checkbox checked={toArray(filters.statoPreventivo).includes(item)} size="small" />
                      {item}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Stato Contratto</InputLabel>
                <Select
                  multiple
                  value={filters.statoContratto}
                  label="Stato Contratto"
                  onChange={(e) => applyFilters({ ...filters, statoContratto: e.target.value })}
                  renderValue={(selected) => {
                    const values = toArray(selected);
                    if (values.length === 0) return 'Tutti';
                    return values.join(', ');
                  }}
                >
                  {statoContrattoOptions.map((item) => (
                    <MenuItem key={item} value={item}>
                      <Checkbox checked={toArray(filters.statoContratto).includes(item)} size="small" />
                      {item}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={3}>
              <Autocomplete
                size="small"
                options={respCommOptions}
                value={filters.respComm || null}
                onChange={(_e, value) => applyFilters({ ...filters, respComm: value || '' })}
                renderInput={(params) => <TextField {...params} label="Resp. commerciale" />}
                clearOnEscape
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <Autocomplete
                size="small"
                options={respUpsellOptions}
                value={filters.respUpsell || null}
                onChange={(_e, value) => applyFilters({ ...filters, respUpsell: value || '' })}
                renderInput={(params) => <TextField {...params} label="Resp. upsell" />}
                clearOnEscape
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <TextField
                fullWidth
                size="small"
                type="date"
                label="Data conf. da"
                value={filters.dataConferimentoFrom}
                onChange={(e) =>
                  applyFilters({ ...filters, dataConferimentoFrom: e.target.value })
                }
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <TextField
                fullWidth
                size="small"
                type="date"
                label="Data conf. a"
                value={filters.dataConferimentoTo}
                onChange={(e) =>
                  applyFilters({ ...filters, dataConferimentoTo: e.target.value })
                }
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <TextField
                fullWidth
                size="small"
                type="date"
                label="Invio doc. da"
                value={filters.dataInvioDocumentiFrom}
                onChange={(e) =>
                  applyFilters({ ...filters, dataInvioDocumentiFrom: e.target.value })
                }
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <TextField
                fullWidth
                size="small"
                type="date"
                label="Invio doc. a"
                value={filters.dataInvioDocumentiTo}
                onChange={(e) =>
                  applyFilters({ ...filters, dataInvioDocumentiTo: e.target.value })
                }
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Stato Documenti</InputLabel>
                <Select
                  value={filters.statoDocumenti || ''}
                  label="Stato Documenti"
                  onChange={(e) => applyFilters({ ...filters, statoDocumenti: e.target.value })}
                >
                  <MenuItem value="">Tutti</MenuItem>
                  <MenuItem value="completi">Completi (tutti caricati)</MenuItem>
                  <MenuItem value="parziali">Caricati parzialmente</MenuItem>
                  <MenuItem value="mancanti">Mancanti</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </Box>
        )}

        {activeFilters.length > 0 && (
          <Box
            sx={{
              px: 3,
              py: 1.25,
              borderBottom: '1px solid',
              borderColor: 'divider',
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              flexWrap: 'wrap',
            }}
          >
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mr: 0.5 }}>
              Filtri attivi
            </Typography>
            {activeFilters.map((chip) => (
              <Chip
                key={chip.key}
                size="small"
                label={chip.label}
                onDelete={() => handleDeleteActiveFilter(chip.key)}
                sx={(theme) => ({
                  fontWeight: 600,
                  bgcolor: theme.palette.mode === 'dark' ? 'rgba(148, 163, 184, 0.24)' : '#F3F4F6',
                  color: theme.palette.text.primary,
                  border: '1px solid',
                  borderColor:
                    theme.palette.mode === 'dark'
                      ? 'rgba(148, 163, 184, 0.45)'
                      : 'rgba(148, 163, 184, 0.35)',
                  '& .MuiChip-deleteIcon': {
                    color: theme.palette.text.secondary,
                  },
                  '& .MuiChip-deleteIcon:hover': {
                    color: theme.palette.text.primary,
                  },
                })}
              />
            ))}
          </Box>
        )}

        <Box sx={{ px: 3, borderBottom: '1px solid', borderColor: 'divider' }}>
          <Tabs
            value={viewTab}
            onChange={(_event, nextTab) => setViewTab(nextTab)}
            textColor="primary"
            indicatorColor="primary"
          >
            <Tab value="list" label="Lista" />
            <Tab value="distribution" label="Distribuzione per commerciale" />
            <Tab value="trend" label="Trend mensile" />
            <Tab value="year-comparison" label="Confronto annuale" />
          </Tabs>
        </Box>

        {viewTab === 'list' ? (
          <>
            <TableContainer>
              <Table
                sx={(theme) => ({
                  '& .MuiTableCell-root': {
                    borderColor: 'divider',
                    color: theme.palette.text.primary,
                  },
                  '& .MuiTableHead-root .MuiTableCell-root': {
                    fontWeight: 600,
                    color: theme.palette.text.primary,
                    letterSpacing: 0
                  },
                  '& .MuiTableSortLabel-root': {
                    color: `${theme.palette.text.primary} !important`,
                    fontWeight: 600
                  },
                  '& .MuiTableSortLabel-icon': {
                    color: `${theme.palette.text.secondary} !important`
                  },
                  '& .MuiCollapse-wrapperInner': {
                    width: '100%'
                  }
                })}
              >
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ width: 44 }} />
                    <TableCell sx={{ width: 44 }} />
                    {columnVisibility.responsabileCommerciale && <TableCell>
                      <TableSortLabel
                        active={orderBy === 'responsabile_commerciale'}
                        direction={orderBy === 'responsabile_commerciale' ? order : 'asc'}
                        onClick={() => handleRequestSort('responsabile_commerciale')}
                      >
                        {twoLineHeader('Resp.', 'commerciale')}
                      </TableSortLabel>
                    </TableCell>}
                    {columnVisibility.responsabileUpsell && <TableCell>
                      <TableSortLabel
                        active={orderBy === 'responsabile_upsell'}
                        direction={orderBy === 'responsabile_upsell' ? order : 'asc'}
                        onClick={() => handleRequestSort('responsabile_upsell')}
                      >
                        {twoLineHeader('Resp.', 'upsell')}
                      </TableSortLabel>
                    </TableCell>}
                    {columnVisibility.customer && <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      <TableSortLabel
                        active={orderBy === 'customer'}
                        direction={orderBy === 'customer' ? order : 'asc'}
                        onClick={() => handleRequestSort('customer')}
                      >
                        Contatto
                      </TableSortLabel>
                    </TableCell>}
                    {columnVisibility.tipoContratto && <TableCell>
                      <TableSortLabel
                        active={orderBy === 'tipo_contratto'}
                        direction={orderBy === 'tipo_contratto' ? order : 'asc'}
                        onClick={() => handleRequestSort('tipo_contratto')}
                      >
                        {twoLineHeader('Tipo', 'contratto')}
                      </TableSortLabel>
                    </TableCell>}
                    {columnVisibility.statoPreventivo && <TableCell>
                      <TableSortLabel
                        active={orderBy === 'stato_preventivo'}
                        direction={orderBy === 'stato_preventivo' ? order : 'asc'}
                        onClick={() => handleRequestSort('stato_preventivo')}
                      >
                        {twoLineHeader('Stato', 'preventivo')}
                      </TableSortLabel>
                    </TableCell>}
                    {columnVisibility.importoTotale && <TableCell>
                      <TableSortLabel
                        active={orderBy === 'importo_totale'}
                        direction={orderBy === 'importo_totale' ? order : 'asc'}
                        onClick={() => handleRequestSort('importo_totale')}
                      >
                        {twoLineHeader('Importo', 'totale')}
                      </TableSortLabel>
                    </TableCell>}
                    {columnVisibility.contrattiImporto && (
                      <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                        Contratti
                      </TableCell>
                    )}
                    {columnVisibility.upsellImporto && (
                      <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                        Upsell
                      </TableCell>
                    )}
                    {columnVisibility.incassatoImporto && (
                      <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                        Incassato
                      </TableCell>
                    )}
                    {columnVisibility.incassatoPercent && (
                      <TableCell align="center" sx={{ whiteSpace: 'nowrap' }}>
                        % sul totale
                      </TableCell>
                    )}
                    {columnVisibility.saldoDaIncassare && (
                      <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                        Saldo
                      </TableCell>
                    )}
                    {columnVisibility.acconto && <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      <TableSortLabel
                        active={orderBy === 'acconto'}
                        direction={orderBy === 'acconto' ? order : 'asc'}
                        onClick={() => handleRequestSort('acconto')}
                      >
                        Acconto
                      </TableSortLabel>
                    </TableCell>}
                    {columnVisibility.percentageTransition && <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      <TableSortLabel
                        active={orderBy === 'percentage_transition'}
                        direction={orderBy === 'percentage_transition' ? order : 'asc'}
                        onClick={() => handleRequestSort('percentage_transition')}
                      >
                        {twoLineHeader('% su', 'Transazione')}
                      </TableSortLabel>
                    </TableCell>}
                    {columnVisibility.dataInvioDocumenti && <TableCell>
                      <TableSortLabel
                        active={orderBy === 'data_invio_documenti'}
                        direction={orderBy === 'data_invio_documenti' ? order : 'asc'}
                        onClick={() => handleRequestSort('data_invio_documenti')}
                      >
                        {twoLineHeader('Data invio', 'documenti')}
                      </TableSortLabel>
                    </TableCell>}
                    {columnVisibility.dataConferimento && <TableCell>
                      <TableSortLabel
                        active={orderBy === 'data_conferimento'}
                        direction={orderBy === 'data_conferimento' ? order : 'asc'}
                        onClick={() => handleRequestSort('data_conferimento')}
                      >
                        {twoLineHeader('Data', 'conferimento')}
                      </TableSortLabel>
                    </TableCell>}
                    {columnVisibility.dataChiusuraConferimento && <TableCell>
                      <TableSortLabel
                        active={orderBy === 'data_chiusura_conferimento'}
                        direction={orderBy === 'data_chiusura_conferimento' ? order : 'asc'}
                        onClick={() => handleRequestSort('data_chiusura_conferimento')}
                      >
                        {twoLineHeader('Data chiusura', 'conferimento')}
                      </TableSortLabel>
                    </TableCell>}
                    {columnVisibility.funnelixConvalida && (
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                        Convalida
                      </TableCell>
                    )}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell colSpan={Math.max(totalColumns, 1)} align="center" sx={{ py: 4 }}>
                        <LoaderProgress size={48} />
                      </TableCell>
                    </TableRow>
                  ) : rows.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={Math.max(totalColumns, 1)} align="center" sx={{ py: 3 }}>
                        Nessun dossier trovato
                      </TableCell>
                    </TableRow>
                  ) : (
                    rows.map((row) => (
                      <Fragment key={row.id}>
                        <TableRow hover sx={(theme) => ({ '&:hover td': { bgcolor: theme.palette.action.hover } })}>
                          <TableCell sx={{ width: 44 }}>
                            {(() => {
                              const meta = richiestaDocStatusMeta(
                                row?.richiesta_doc_stato,
                                row?.richiesta_doc_all_present,
                                row?.richiesta_doc_type_cnt,
                                isAziendaCustomer(row) ? 4 : 3
                              );
                              const Icon = meta.Icon;
                              return (
                                <Tooltip title={meta.label}>
                                  <IconButton
                                    size="small"
                                    onClick={() => openRichiestaDocDialog(row)}
                                    sx={{ p: 0.25, color: meta.color }}
                                  >
                                    <Icon size={18} />
                                  </IconButton>
                                </Tooltip>
                              );
                            })()}
                          </TableCell>
                          <TableCell sx={{ width: 44 }}>
                            <Tooltip title="Mostra pratiche collegate">
                              <IconButton size="small" onClick={() => handleToggleRowDetails(row.id)}>
                                {expandedRows[row.id] ? <KeyboardArrowUpIcon fontSize="small" /> : <KeyboardArrowDownIcon fontSize="small" />}
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                          {columnVisibility.responsabileCommerciale && <TableCell sx={{ py: 1.5 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <User size={15} color="currentColor" />
                              <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary' }}>
                                {row.responsabile_commerciale_nome || '-'}
                              </Typography>
                            </Box>
                          </TableCell>}
                          {columnVisibility.responsabileUpsell && <TableCell sx={{ py: 1.5, color: 'text.primary', fontWeight: 600 }}>{row.responsabile_upsell_nome || '-'}</TableCell>}
                          {columnVisibility.customer && <TableCell>
                            <Box sx={{ minWidth: 260, display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                              <Box
                                sx={{
                                  width: 28,
                                  height: 28,
                                  borderRadius: '50%',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  bgcolor: 'rgba(37, 99, 235, 0.08)',
                                  color: '#2563eb',
                                  mt: 0.1,
                                  flexShrink: 0
                                }}
                              >
                                <User size={16} />
                              </Box>
                              <Box>
                                <Typography
                                  variant="body2"
                                  sx={{
                                    fontWeight: 600,
                                    color: 'text.primary',
                                    lineHeight: 1.15
                                  }}
                                >
                                  {getCustomerDisplayName(row)}
                                </Typography>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.15 }}>
                                  <Phone size={12} color="#64748b" />
                                  <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.15 }}>
                                    {row.customer_telephone || '-'}
                                  </Typography>
                                </Box>
                                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.15, display: 'block', lineHeight: 1.15 }}>
                                  {row.customer_email || '-'}
                                </Typography>
                              </Box>
                            </Box>
                          </TableCell>}
                          {columnVisibility.tipoContratto && <TableCell sx={{ color: 'text.primary', fontWeight: 500 }}>{row.tipo_contratto || '-'}</TableCell>}
                          {columnVisibility.statoPreventivo && (
                            <TableCell sx={{ whiteSpace: 'nowrap' }}>
                              <Chip
                                size="small"
                                label={row.stato_preventivo || '-'}
                                sx={(theme) => ({
                                  height: 24,
                                  borderRadius: 999,
                                  bgcolor: row.stato_preventivo
                                    ? (theme.palette.mode === 'dark' ? alpha(theme.palette.primary.main, 0.22) : '#eef2ff')
                                    : (theme.palette.mode === 'dark' ? alpha(theme.palette.common.white, 0.08) : '#f8fafc'),
                                  color: row.stato_preventivo ? theme.palette.primary.main : theme.palette.text.secondary,
                                  border: '1px solid',
                                  borderColor: row.stato_preventivo ? alpha(theme.palette.primary.main, 0.45) : theme.palette.divider,
                                  fontWeight: 600,
                                  '& .MuiChip-label': { px: 1.2 }
                                })}
                              />
                            </TableCell>
                          )}
                          {columnVisibility.importoTotale && (
                            <TableCell>
                              <Typography variant="body2" sx={{ fontWeight: 700, color: getMoneyColor(row.importo_totale) }}>
                                {formatMoney(row.importo_totale)}
                              </Typography>
                              <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 500, mt: 0.1, display: 'block', lineHeight: 1.15 }}>
                                {`${Number(row.number_installment || 0)} rate`}
                              </Typography>
                            </TableCell>
                          )}
                          {columnVisibility.contrattiImporto && (
                            <TableCell align="right">
                              <Typography variant="body2" sx={{ fontWeight: 700, color: getMoneyColor(row.contratti_importo) }}>
                                {formatMoney(row.contratti_importo)}
                              </Typography>
                            </TableCell>
                          )}
                          {columnVisibility.upsellImporto && (
                            <TableCell align="right">
                              <Typography variant="body2" sx={{ fontWeight: 700, color: getMoneyColor(row.upsell_importo) }}>
                                {formatMoney(row.upsell_importo)}
                              </Typography>
                            </TableCell>
                          )}
                          {columnVisibility.incassatoImporto && (
                            <TableCell align="right">
                              <Typography variant="body2" sx={{ fontWeight: 700, color: getMoneyColor(row.incassato_importo) }}>
                                {formatMoney(row.incassato_importo)}
                              </Typography>
                            </TableCell>
                          )}
                          {columnVisibility.incassatoPercent && (
                            <TableCell align="center">
                              {row.incassato_percent == null ? '-' : renderPercentRing(row.incassato_percent)}
                            </TableCell>
                          )}
                          {columnVisibility.saldoDaIncassare && (
                            <TableCell align="right">
                              <Typography variant="body2" sx={{ fontWeight: 800, color: getMoneyColor(row.saldo_da_incassare) }}>
                                {formatMoney(row.saldo_da_incassare)}
                              </Typography>
                            </TableCell>
                          )}
                          {columnVisibility.acconto && (
                            <TableCell>
                              <Typography variant="body2" sx={{ fontWeight: 700, color: getMoneyColor(row.acconto) }}>
                                {formatMoney(row.acconto)}
                              </Typography>
                            </TableCell>
                          )}
                          {columnVisibility.percentageTransition && <TableCell sx={{ color: 'text.primary', fontWeight: 600 }}>{formatPercent(row.percentage_transition)}</TableCell>}
                          {columnVisibility.dataInvioDocumenti && <TableCell sx={{ color: 'text.primary', whiteSpace: 'nowrap' }}>{formatDate(row.data_invio_documenti)}</TableCell>}
                          {columnVisibility.dataConferimento && <TableCell sx={{ color: 'text.primary', fontWeight: 500, whiteSpace: 'nowrap' }}>{formatDate(row.data_conferimento)}</TableCell>}
                          {columnVisibility.dataChiusuraConferimento && <TableCell sx={{ color: 'text.primary', whiteSpace: 'nowrap' }}>{formatDate(row.data_chiusura_conferimento)}</TableCell>}
                          {columnVisibility.funnelixConvalida && (
                            <TableCell sx={{ whiteSpace: 'nowrap' }}>
                              {firmeValidationMap[row.id] ? (
                                <Tooltip
                                  title={
                                    firmeValidationMap[row.id].found
                                      ? `Trovato su Funnelix (${firmeValidationMap[row.id].matchBy === 'email' ? 'email' : 'telefono'})`
                                      : (firmeValidationMap[row.id].reason === 'no_key' ? 'Senza email/telefono' : 'Non trovato su Funnelix')
                                  }
                                >
                                  <Chip
                                    size="small"
                                    color={
                                      firmeValidationMap[row.id].found
                                        ? 'success'
                                        : (firmeValidationMap[row.id].reason === 'no_key' ? 'warning' : 'default')
                                    }
                                    variant={firmeValidationMap[row.id].found ? 'filled' : 'outlined'}
                                    label={
                                      firmeValidationMap[row.id].found
                                        ? 'Trovato'
                                        : (firmeValidationMap[row.id].reason === 'no_key' ? 'Senza contatti' : 'Non trovato')
                                    }
                                    sx={{ height: 24, borderRadius: 999, fontWeight: 600 }}
                                  />
                                </Tooltip>
                              ) : (
                                <Typography variant="body2" color="text.secondary">
                                  -
                                </Typography>
                              )}
                            </TableCell>
                          )}
                        </TableRow>
                        <TableRow>
                          <TableCell colSpan={totalColumns} sx={{ py: 0, borderBottom: expandedRows[row.id] ? undefined : 'none' }}>
                            <Collapse in={!!expandedRows[row.id]} timeout="auto" unmountOnExit>
                              <Box sx={{ py: 1.5 }}>
                                {rowNegotiationsLoading[row.id] ? (
                                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 1 }}>
                                    <CircularProgress size={22} />
                                  </Box>
                                ) : (rowNegotiations[row.id] || []).length === 0 ? (
                                  <Typography variant="body2" color="text.secondary">
                                    Nessuna pratica collegata trovata per questo dossier.
                                  </Typography>
                                ) : (
                                  <Box>
                                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                                      Pratiche collegate
                                    </Typography>
                                    <Table size="small">
                                      <TableHead>
                                        <TableRow>
                                          <TableCell>Controparte</TableCell>
                                          <TableCell>Tipologia</TableCell>
                                          <TableCell>{twoLineHeader('Data assegnazione', 'legale')}</TableCell>
                                          <TableCell>{twoLineHeader('Rif. responsabile', 'contabile')}</TableCell>
                                          <TableCell>{twoLineHeader('Rif. responsabile', 'legale')}</TableCell>
                                          <TableCell>Status</TableCell>
                                        </TableRow>
                                      </TableHead>
                                      <TableBody>
                                        {(rowNegotiations[row.id] || []).map((neg) => (
                                          <TableRow key={neg.id} hover>
                                            <TableCell>{neg.issuer_name || '-'}</TableCell>
                                            <TableCell>{neg.category_name || '-'}</TableCell>
                                            <TableCell>{formatDateTime(neg.data_assegnazione_legale)}</TableCell>
                                            <TableCell>{neg.responsabile_contabile_name || neg.rif_responsabile_contabile || '-'}</TableCell>
                                            <TableCell>{neg.responsabile_legale_name || neg.rif_responsabile_legale || '-'}</TableCell>
                                            <TableCell>
                                              <Chip size="small" label={neg.status || '-'} variant="outlined" />
                                            </TableCell>
                                          </TableRow>
                                        ))}
                                      </TableBody>
                                    </Table>
                                  </Box>
                                )}
                              </Box>
                            </Collapse>
                          </TableCell>
                        </TableRow>
                      </Fragment>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            <TablePagination
              component="div"
              count={total}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={[10, 25, 50, 100]}
              labelRowsPerPage="Righe per pagina"
            />
          </>
        ) : viewTab === 'distribution' ? (
          <Box sx={{ p: 2.5 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                Distribuzione per referente commerciale
              </Typography>
              <Chip size="small" label="Filtri attivi" color="primary" variant="filled" />
            </Box>
            <Box sx={{ height: distributionChartHeight }}>
              {loading ? (
                <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <LoaderProgress size={48} />
                </Box>
              ) : commercialeDistributionChartData.length === 0 ? (
                <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'text.secondary' }}>
                  Nessun dato disponibile per il grafico con i filtri correnti.
                </Box>
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={commercialeDistributionChartData}
                    layout="vertical"
                    margin={{ top: 10, right: 20, bottom: 10, left: distributionLeftMargin }}
                  >
                    <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                    <XAxis type="number" />
                    <YAxis type="category" dataKey="name" width={distributionYAxisWidth} interval={0} />
                    <RechartsTooltip />
                    <Bar dataKey="value" fill="#4f46e5" radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </Box>
          </Box>
        ) : viewTab === 'trend' ? (
          <Box sx={{ p: 2.5 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                Trend giornaliero mese {trendMonthLabel || '-'}
              </Typography>
              <Chip
                size="small"
                label={`Media giornaliera: ${monthlyTrendAverage.toFixed(1)}`}
                color="default"
                variant="outlined"
              />
            </Box>
            <Box sx={{ height: 320 }}>
              {loading ? (
                <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <LoaderProgress size={48} />
                </Box>
              ) : monthlyTrendChartData.length === 0 ? (
                <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'text.secondary' }}>
                  Nessun dato disponibile per il trend mensile con i filtri correnti.
                </Box>
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={monthlyTrendChartDataWithAvg} margin={{ top: 8, right: 20, bottom: 26, left: 24 }}>
                    <CartesianGrid strokeDasharray="4 4" vertical={false} stroke="#d1d5db" />
                    <XAxis
                      dataKey="dayLabel"
                      interval={0}
                      tick={{ fontSize: 10, fill: '#6b7280' }}
                      angle={-45}
                      textAnchor="end"
                      height={50}
                      label={{ value: 'Giorni del mese', position: 'insideBottom', offset: -8 }}
                      axisLine={{ stroke: '#9ca3af' }}
                      tickLine={{ stroke: '#9ca3af' }}
                    />
                    <YAxis
                      allowDecimals={false}
                      tick={{ fill: '#6b7280' }}
                      tickFormatter={(value) => Number(value || 0).toLocaleString('it-IT')}
                      label={{ value: 'Totale contratti', angle: -90, position: 'insideLeft' }}
                      axisLine={{ stroke: '#9ca3af' }}
                      tickLine={{ stroke: '#9ca3af' }}
                    />
                    <RechartsTooltip
                      formatter={(value, name) => [
                        name === 'Media giornaliera'
                          ? Number(value || 0).toFixed(1)
                          : Number(value || 0),
                        name
                      ]}
                      labelFormatter={(label) => `Giorno ${label}`}
                    />
                    <Legend />
                    <Line
                      type="monotone"
                      dataKey="value"
                      name="Totale contratti"
                      stroke="#22c55e"
                      strokeWidth={4}
                      dot={{ r: 4, fill: '#22c55e', stroke: '#ffffff', strokeWidth: 2 }}
                      activeDot={{ r: 6, fill: '#22c55e', stroke: '#ffffff', strokeWidth: 2 }}
                    />
                    <Line
                      type="linear"
                      dataKey="avg"
                      name="Media giornaliera"
                      stroke="#f59e0b"
                      strokeWidth={2}
                      strokeDasharray="6 4"
                      dot={false}
                      activeDot={false}
                    />
                  </LineChart>
                </ResponsiveContainer>
              )}
            </Box>
          </Box>
        ) : (
          <Box sx={{ p: 2.5 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                Confronto annuale {yearComparison.currentYear} vs {yearComparison.previousYear}
              </Typography>
              <Chip
                size="small"
                label={`${yearComparison.currentYear} vs ${yearComparison.previousYear}`}
                color="default"
                variant="outlined"
              />
            </Box>
            <Box sx={{ height: 340 }}>
              {loading ? (
                <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <LoaderProgress size={48} />
                </Box>
              ) : yearComparisonChartData.length === 0 ? (
                <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'text.secondary' }}>
                  Nessun dato disponibile per il confronto annuale.
                </Box>
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={yearComparisonChartData} margin={{ top: 12, right: 18, left: 10, bottom: 12 }}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} />
                    <XAxis dataKey="monthLabel" />
                    <YAxis allowDecimals={false} />
                    <RechartsTooltip formatter={(value) => [value, 'Totale contratti']} />
                    <Legend />
                    <Bar
                      name={String(yearComparison.currentYear)}
                      dataKey="currentYearTotal"
                      fill="#22c55e"
                      radius={[6, 6, 0, 0]}
                      maxBarSize={24}
                    />
                    <Bar
                      name={String(yearComparison.previousYear)}
                      dataKey="previousYearTotal"
                      fill="#e11d48"
                      radius={[6, 6, 0, 0]}
                      maxBarSize={24}
                    />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </Box>
          </Box>
        )}
      </StyledCard>

      <Dialog
        open={richiestaDocDialogOpen}
        onClose={closeRichiestaDocDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: '16px', overflow: 'hidden' } }}
      >
        <DialogTitle sx={{ pb: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: '50%',
                bgcolor: '#eff6ff',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <FileText size={18} color="#2563eb" />
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="h6" sx={{ fontWeight: 800, lineHeight: 1.1 }}>
                Richiesta documenti
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Gestisci file richiesta
              </Typography>
            </Box>
          </Box>
          {richiestaDocIsPartial && (
            <Tooltip title={`Caricati parzialmente (${richiestaDocPresentCount}/${richiestaDocRequiredTotal})`} arrow>
              <Box
                sx={{
                  width: 32,
                  height: 32,
                  borderRadius: '50%',
                  bgcolor: '#f59e0b1F',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0
                }}
              >
                <AlertTriangle size={18} color="#f59e0b" />
              </Box>
            </Tooltip>
          )}
        </DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.75, mb: 1.5 }}>
            <Typography variant="body2" color="text.secondary">
              Seleziona uno o più file. Il sistema assegna automaticamente il tipo in base al nome:{' '}
              {richiestaDocIsAzienda ? 'contratto / mandato / doc id / visura / questionario' : 'contratto / mandato / doc id / questionario'}.
              Il nome viene mantenuto identico su Wasabi.
            </Typography>
            <Tooltip
              arrow
              placement="top"
              title={
                <Box sx={{ maxWidth: 360 }}>
                  <Typography variant="caption" sx={{ display: 'block', fontWeight: 800 }}>
                    Suggerimenti
                  </Typography>
                  <Typography variant="caption" sx={{ display: 'block' }}>
                    - Il tipo viene riconosciuto dal nome file (
                    {richiestaDocIsAzienda ? 'contratto / mandato / doc id / visura / questionario' : 'contratto / mandato / doc id / questionario'}).
                  </Typography>
                  <Typography variant="caption" sx={{ display: 'block' }}>
                    - L’anteprima supporta PDF e immagini.
                  </Typography>
                  <Typography variant="caption" sx={{ display: 'block' }}>
                    - “Elimina” rimuove il file già caricato su Wasabi.
                  </Typography>
                </Box>
              }
            >
              <IconButton size="small" sx={{ p: 0.5, mt: '2px' }}>
                <HelpCircle size={16} />
              </IconButton>
            </Tooltip>
          </Box>

          <input
            ref={richiestaDocFileRef}
            type="file"
            multiple
            onChange={handleRichiestaDocFilesSelected}
            style={{ display: 'none' }}
          />

          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 2 }}>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 800, lineHeight: 1.15 }}>
                {getCustomerDisplayName(richiestaDocTarget || {})}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Dossier ID: {richiestaDocTarget?.id != null ? String(richiestaDocTarget.id) : '-'}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {richiestaDocExistingLoading && <CircularProgress size={18} />}
              <Button
                variant="contained"
                onClick={handlePickRichiestaDocFiles}
                startIcon={<FileText size={16} />}
                disabled={richiestaDocSaving || richiestaDocExistingLoading}
                sx={{ borderRadius: '8px' }}
              >
                Seleziona file
              </Button>
            </Box>
          </Box>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
            {richiestaDocAllTypes.map((type) => {
              const file = richiestaDocFiles?.[type] || null;
              const isOptional = richiestaDocOptionalTypes.includes(type);
              const existingName =
                type === 'contratto'
                  ? richiestaDocExisting?.contratto_file_name
                  : type === 'mandato'
                    ? richiestaDocExisting?.mandato_file_name
                    : type === 'doc_identita'
                      ? richiestaDocExisting?.doc_identita_file_name
                      : type === 'visura'
                        ? richiestaDocExisting?.visura_file_name
                        : richiestaDocExisting?.questionario_file_name;
              const displayName = file?.name || existingName || 'Non selezionato';
              const chipLabel = file ? 'Nuovo' : existingName ? 'Caricato' : '—';
              const chipColor = file ? 'warning' : existingName ? 'success' : 'default';
              return (
                <Paper
                  key={type}
                  variant="outlined"
                  sx={(theme) => ({
                    p: 1.25,
                    borderRadius: '8px',
                    borderColor: theme.palette.mode === 'dark' ? alpha(theme.palette.common.white, 0.14) : '#e2e8f0'
                  })}
                >
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1 }}>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="body2" sx={{ fontWeight: 800 }}>
                        {richiestaDocTypeLabel(type)}
                      </Typography>
                      <Typography
                        variant="caption"
                        color={file || existingName ? 'text.primary' : 'text.secondary'}
                        sx={{ display: 'block', overflowWrap: 'anywhere', wordBreak: 'break-word' }}
                      >
                        {displayName}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
                      {isOptional && (
                        <Chip
                          size="small"
                          label="Opzionale"
                          color="default"
                          variant="outlined"
                        />
                      )}
                      <Chip
                        size="small"
                        label={chipLabel}
                        color={chipColor}
                        variant={file || existingName ? 'filled' : 'outlined'}
                      />
                      <Tooltip
                        arrow
                        placement="top"
                        title={
                          file
                            ? 'File selezionato ora (non ancora salvato su Wasabi)'
                            : existingName
                              ? 'File già caricato su Wasabi'
                              : 'Nessun file associato'
                        }
                      >
                        <IconButton size="small" sx={{ p: 0.25 }}>
                          <HelpCircle size={16} />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Apri anteprima">
                        <span>
                          <IconButton
                            size="small"
                            onClick={() => handleOpenRichiestaDocPreview(type)}
                            disabled={!Boolean(file || existingName) || richiestaDocSaving || richiestaDocExistingLoading}
                            sx={{ p: 0.5 }}
                          >
                            <Eye size={16} />
                          </IconButton>
                        </span>
                      </Tooltip>
                      {file && (
                        <Button
                          size="small"
                          onClick={() => removeRichiestaDocFile(type)}
                          disabled={richiestaDocSaving}
                          sx={{ borderRadius: '8px' }}
                        >
                          Rimuovi
                        </Button>
                      )}
                      {!file && existingName && (
                        <Tooltip arrow placement="top" title="Elimina definitivamente il file da Wasabi">
                          <span>
                            <IconButton
                              size="small"
                              aria-label="Elimina"
                              onClick={() => deleteRichiestaDocFile(type)}
                              disabled={richiestaDocSaving || richiestaDocDeleting || richiestaDocExistingLoading}
                              sx={(theme) => ({
                                color: theme.palette.mode === 'dark' ? '#fca5a5' : '#dc2626',
                                '&:hover': { bgcolor: theme.palette.mode === 'dark' ? alpha('#dc2626', 0.22) : alpha('#dc2626', 0.10) }
                              })}
                            >
                              <Trash2 size={16} />
                            </IconButton>
                          </span>
                        </Tooltip>
                      )}
                    </Box>
                  </Box>
                </Paper>
              );
            })}
          </Box>

          {Object.keys(richiestaDocDuplicates || {}).filter((k) => (richiestaDocDuplicates?.[k] || []).length > 0).length > 0 && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.75 }}>
                File duplicati ignorati (per tipo viene usato il primo trovato)
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                {Object.entries(richiestaDocDuplicates || {})
                  .filter(([, list]) => Array.isArray(list) && list.length > 0)
                  .map(([type, list]) => (
                    <Typography key={type} variant="body2" sx={{ overflowWrap: 'anywhere', wordBreak: 'break-word' }}>
                      {richiestaDocTypeLabel(type)}: {(list || []).map((f) => f?.name).filter(Boolean).join(', ')}
                    </Typography>
                  ))}
              </Box>
            </Box>
          )}

          {Array.isArray(richiestaDocUnmatched) && richiestaDocUnmatched.length > 0 && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.75 }}>
                File non riconosciuti (nome non contiene {richiestaDocIsAzienda ? 'contratto/mandato/doc id/visura/questionario' : 'contratto/mandato/doc id/questionario'})
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                {richiestaDocUnmatched.map((f, idx) => (
                  <Typography key={`${f?.name || 'file'}-${idx}`} variant="body2" sx={{ overflowWrap: 'anywhere', wordBreak: 'break-word' }}>
                    {f?.name || 'file'}
                  </Typography>
                ))}
              </Box>
            </Box>
          )}

          <Box sx={{ mt: 2 }}>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
              Tipi riconosciuti: {richiestaDocAllTypes.map(richiestaDocTypeLabel).join(' • ')}
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          {richiestaDocExisting?.id && (
            <Button
              color="error"
              onClick={openDeleteRichiestaConfirm}
              disabled={richiestaDocSaving || richiestaDocDeleting || richiestaDocExistingLoading}
              sx={{ mr: 'auto', borderRadius: '8px' }}
            >
              Elimina richiesta
            </Button>
          )}
          <Button onClick={closeRichiestaDocDialog} disabled={richiestaDocSaving} sx={{ borderRadius: '8px' }}>
            Annulla
          </Button>
          <Button
            onClick={submitRichiestaDoc}
            variant="contained"
            disabled={
              richiestaDocSaving ||
              richiestaDocExistingLoading ||
              (!richiestaDocAllTypes.some((type) => Boolean(richiestaDocFiles?.[type])) &&
                !Boolean(richiestaDocExisting?.id || richiestaDocExisting?.is_draft))
            }
            sx={{ borderRadius: '8px' }}
          >
            {(() => {
              if (richiestaDocSaving) return 'Salvataggio...';
              const hasAnyNew = richiestaDocAllTypes.some((type) => Boolean(richiestaDocFiles?.[type]));
              if (hasAnyNew) return 'Salva';
              if (richiestaDocExisting?.id || richiestaDocExisting?.is_draft) return 'Chiudi';
              return 'Conferma';
            })()}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={richiestaDocDeleteConfirmOpen}
        onClose={() => !richiestaDocDeleting && setRichiestaDocDeleteConfirmOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: '2px' } }}
      >
        <DialogTitle>Elimina richiesta</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary">
            Verranno eliminati i riferimenti della richiesta e tutti i file collegati su Wasabi.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRichiestaDocDeleteConfirmOpen(false)} disabled={richiestaDocDeleting}>
            Annulla
          </Button>
          <Button variant="contained" color="error" onClick={deleteRichiestaDoc} disabled={richiestaDocDeleting} sx={{ borderRadius: '2px' }}>
            {richiestaDocDeleting ? 'Eliminazione...' : 'Elimina'}
          </Button>
        </DialogActions>
      </Dialog>

      <Drawer
        anchor="right"
        open={richiestaDocPreviewOpen}
        onClose={handleCloseRichiestaDocPreview}
        hideBackdrop
        sx={{ zIndex: (theme) => theme.zIndex.modal + 2 }}
        PaperProps={{
          sx: {
            width: { xs: '100vw', sm: 480 },
            maxWidth: '100vw',
            borderLeft: '1px solid rgba(15, 23, 42, 0.08)',
            boxShadow: '0 0 0 1px rgba(15, 23, 42, 0.06), 0 24px 48px rgba(15, 23, 42, 0.24)'
          }
        }}
      >
        <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
          <Box
            sx={{
              p: 2,
              borderBottom: '1px solid rgba(145, 158, 171, 0.24)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 1
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                Anteprima documento
              </Typography>
              <Tooltip arrow placement="bottom" title="Supporta PDF e immagini. Usa “Apri in nuova scheda” per aprire a schermo intero.">
                <IconButton size="small" sx={{ p: 0.25 }}>
                  <HelpCircle size={16} />
                </IconButton>
              </Tooltip>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Button
                size="small"
                variant="outlined"
                startIcon={<ExternalLink size={14} />}
                onClick={handleOpenRichiestaDocPreviewInNewTab}
                disabled={!richiestaDocPreviewRow || richiestaDocPreviewOpenInNewTabLoading}
                sx={{
                  height: 40,
                  minHeight: 40,
                  px: 2,
                  borderRadius: '12px',
                  fontWeight: 800,
                  textTransform: 'none'
                }}
              >
                {richiestaDocPreviewOpenInNewTabLoading ? 'Apertura...' : 'Apri in nuova scheda'}
              </Button>
              <Button
                size="small"
                variant="text"
                onClick={handleCloseRichiestaDocPreview}
                sx={{ height: 40, minHeight: 40, px: 1.5, borderRadius: '12px', fontWeight: 800, textTransform: 'none' }}
              >
                Chiudi
              </Button>
            </Box>
          </Box>

          <Box sx={{ flex: 1, minHeight: 0, p: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box sx={{ minHeight: 0, flex: 1, overflow: 'auto' }}>
              {richiestaDocPreviewLoading && (
                <Box sx={{ py: 6, display: 'flex', justifyContent: 'center' }}>
                  <CircularProgress size={24} />
                </Box>
              )}

              {!richiestaDocPreviewLoading && richiestaDocPreviewRow && (() => {
                const mime = String(richiestaDocPreviewRow.mimeType || '').toLowerCase();
                const isPdf = mime.includes('pdf');
                const isImage = mime.startsWith('image/');

                if (richiestaDocPreviewUrl && isPdf) {
                  return (
                    <Box sx={{ height: { xs: '55vh', sm: '68vh' }, maxHeight: '78vh' }}>
                      <iframe
                        title={richiestaDocPreviewRow.name || 'Anteprima documento'}
                        src={richiestaDocPreviewUrl}
                        style={{ border: 0, width: '100%', height: '100%' }}
                      />
                    </Box>
                  );
                }

                if (richiestaDocPreviewUrl && isImage) {
                  return (
                    <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                      <img
                        src={richiestaDocPreviewUrl}
                        alt={richiestaDocPreviewRow.name || 'Documento'}
                        style={{ maxWidth: '100%', maxHeight: '72vh', borderRadius: 8 }}
                      />
                    </Box>
                  );
                }

                return (
                  <Box sx={{ py: 6, textAlign: 'center' }}>
                    <Typography variant="body2" color="text.secondary">
                      Anteprima non disponibile per questo tipo di file.
                    </Typography>
                  </Box>
                );
              })()}
            </Box>

            {richiestaDocPreviewRow && (
              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  Dettagli
                </Typography>
                <Typography variant="body2">Nome: {richiestaDocPreviewRow.name || '-'}</Typography>
                <Typography variant="body2">Tipo: {richiestaDocTypeLabel(richiestaDocPreviewRow.type)}</Typography>
                <Typography variant="body2">
                  Formato: {String(richiestaDocPreviewRow.mimeType || '').includes('pdf') ? 'PDF' : String(richiestaDocPreviewRow.mimeType || '').startsWith('image/') ? 'Immagine' : 'Altro'}
                </Typography>
                <Typography variant="body2">Dimensione: {formatFileSize(richiestaDocPreviewRow.sizeBytes)}</Typography>
              </Box>
            )}
          </Box>
        </Box>
      </Drawer>

      <Dialog open={reportDialogOpen} onClose={() => !reportLoading && setReportDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Conferma generazione Report Firme</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            Verrà generato un PDF asincrono con KPI in testa e raggruppamento per commerciale / upsell.
            Il file sarà disponibile nelle notifiche.
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
            {activeFilters.length > 0 ? (
              activeFilters.map((chip) => (
                <Chip key={chip.key} size="small" label={chip.label} />
              ))
            ) : (
              <Chip size="small" label="Nessun filtro applicato" />
            )}
          </Box>
          <Box sx={{ mt: 2 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={includeReportDetail}
                  onChange={(event) => setIncludeReportDetail(event.target.checked)}
                />
              }
              label="Includi il dettaglio contratti nel report PDF"
            />
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', ml: 4 }}>
              Se disattivato, il PDF conterrà solo KPI e riepiloghi per commerciale / upsell.
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReportDialogOpen(false)} disabled={reportLoading}>Annulla</Button>
          <Button onClick={handleStartFirmeReport} variant="contained" disabled={reportLoading}>
            {reportLoading ? 'Avvio...' : 'Genera PDF'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={firmeValidationConfirmOpen}
        onClose={() => !firmeValidationLoading && setFirmeValidationConfirmOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Convalida Firme (Funnelix)</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            Questa operazione confronta i contatti dei dossier filtrati con i contatti importati da Funnelix.
          </Typography>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
            <Box sx={{ display: 'flex', gap: 1.25, alignItems: 'flex-start' }}>
              <Box
                sx={{
                  width: 34,
                  height: 34,
                  borderRadius: '50%',
                  bgcolor: alpha('#2563eb', 0.12),
                  color: '#2563eb',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0
                }}
              >
                <Search size={18} />
              </Box>
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                  Cosa fa
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Verifica la presenza su Funnelix usando solo email e telefono (nessun match per nominativo).
                </Typography>
              </Box>
            </Box>

            <Box sx={{ display: 'flex', gap: 1.25, alignItems: 'flex-start' }}>
              <Box
                sx={{
                  width: 34,
                  height: 34,
                  borderRadius: '50%',
                  bgcolor: alpha('#16a34a', 0.12),
                  color: '#16a34a',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0
                }}
              >
                <Mail size={18} />
              </Box>
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                  Match email
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Confronto su email normalizzata (minuscole, spazi rimossi).
                </Typography>
              </Box>
            </Box>

            <Box sx={{ display: 'flex', gap: 1.25, alignItems: 'flex-start' }}>
              <Box
                sx={{
                  width: 34,
                  height: 34,
                  borderRadius: '50%',
                  bgcolor: alpha('#4f46e5', 0.12),
                  color: '#4f46e5',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0
                }}
              >
                <Phone size={18} />
              </Box>
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                  Match telefono
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Confronto su telefono normalizzato (solo cifre e +, con varianti +39/39 quando possibile).
                </Typography>
              </Box>
            </Box>

            <Box sx={{ display: 'flex', gap: 1.25, alignItems: 'flex-start' }}>
              <Box
                sx={{
                  width: 34,
                  height: 34,
                  borderRadius: '50%',
                  bgcolor: alpha('#0f172a', 0.08),
                  color: '#0f172a',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0
                }}
              >
                <CheckCircle2 size={18} />
              </Box>
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                  Impatto
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Non modifica dati: produce solo un esito (trovato/non trovato/senza contatti) e aggiorna la colonna in tabella.
                </Typography>
              </Box>
            </Box>
          </Box>

          <Box sx={{ mt: 2 }}>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.75 }}>
              Filtri applicati
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
              {activeFilters.length > 0 ? (
                activeFilters.map((chip) => <Chip key={chip.key} size="small" label={chip.label} />)
              ) : (
                <Chip size="small" label="Nessun filtro applicato" />
              )}
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFirmeValidationConfirmOpen(false)} disabled={firmeValidationLoading}>
            Annulla
          </Button>
          <Button onClick={startFirmeValidation} variant="contained" disabled={firmeValidationLoading}>
            {firmeValidationLoading ? 'Convalida...' : 'Avvia convalida'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={firmeValidationDialogOpen}
        onClose={() => !firmeValidationLoading && setFirmeValidationDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Convalida Firme (Funnelix)</DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
            <Chip
              size="small"
              color="success"
              label={`Trovati: ${Number(firmeValidationSummary?.found || 0).toLocaleString('it-IT')}`}
            />
            <Chip
              size="small"
              variant="outlined"
              label={`Email: ${Number(firmeValidationSummary?.foundByEmail || 0).toLocaleString('it-IT')}`}
            />
            <Chip
              size="small"
              variant="outlined"
              label={`Telefono: ${Number(firmeValidationSummary?.foundByPhone || 0).toLocaleString('it-IT')}`}
            />
            <Chip
              size="small"
              color="warning"
              variant="outlined"
              label={`Senza contatti: ${Number(firmeValidationSummary?.missingNoKey || 0).toLocaleString('it-IT')}`}
            />
            <Chip
              size="small"
              color="default"
              variant="outlined"
              label={`Non trovati: ${Number(firmeValidationSummary?.missingNoMatch || 0).toLocaleString('it-IT')}`}
            />
            <Chip
              size="small"
              color="default"
              label={`Totale: ${Number(firmeValidationSummary?.total || 0).toLocaleString('it-IT')}`}
            />
          </Box>
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Non trovati su Funnelix
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              {firmeValidationResults.filter((x) => x && !x.found && x.reason === 'no_match').length > 0 ? (
                firmeValidationResults
                  .filter((x) => x && !x.found && x.reason === 'no_match')
                  .slice(0, 200)
                  .map((x) => (
                    <Typography key={`${x.dossierId}-${x.name}`} variant="body2">
                      {x.name || `Dossier ${x.dossierId}`} {x.email ? `• ${x.email}` : ''} {x.phone ? `• ${x.phone}` : ''}
                    </Typography>
                  ))
              ) : (
                <Typography variant="body2" color="text.secondary">
                  Nessuno
                </Typography>
              )}
            </Box>
          </Box>
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Senza email/telefono
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              {firmeValidationResults.filter((x) => x && !x.found && x.reason === 'no_key').length > 0 ? (
                firmeValidationResults
                  .filter((x) => x && !x.found && x.reason === 'no_key')
                  .slice(0, 200)
                  .map((x) => (
                    <Typography key={`${x.dossierId}-${x.name}`} variant="body2">
                      {x.name || `Dossier ${x.dossierId}`}
                    </Typography>
                  ))
              ) : (
                <Typography variant="body2" color="text.secondary">
                  Nessuno
                </Typography>
              )}
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFirmeValidationDialogOpen(false)} disabled={firmeValidationLoading}>
            Chiudi
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
